import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

import { MembershipV2Service } from 'src/app/services/membership-v2.service';
import { KundeKortService } from 'src/app/services/kunde-kort.service';
import { AvstemmingService } from 'src/app/services/avstemming.service';
import { AuthService } from 'src/app/services/auth.service';
import { GarantiomsetningService, GarantiBillable } from 'src/app/services/garantiomsetning.service';
import { MotematBillingService, MotematBillable } from 'src/app/services/motemat-billing.service';
import { DriftkostnadService, DRIFTKOSTNAD_RATE_EX_VAT } from 'src/app/services/driftkostnad.service';
import { AvtalelinjeService, AvtaleBillable, Avtalelinje, AVTALE_KINDS } from 'src/app/services/avtalelinje.service';
import { SystemReferenceService } from 'src/app/services/system-reference.service';
import { JuiceService, JuiceEntry, JuiceBillable, JUICE_TYPES, JUICE_DEFAULT_PRICE_EX_VAT } from 'src/app/services/juice.service';
import { GarantiomsetningComponent } from 'src/app/admin-dashboard/garantiomsetning/garantiomsetning.component';
import { ConfigService } from 'src/app/shared/config';

// Default VAT applied to invoice lines when a customer (kundekort) has no
// explicit vatRate. Client bills the subsidy as a food sale, so the Norwegian
// reduced (food) rate is the default; VAT-exempt customers store vatRate = 0
// on their kundekort (see vendors.json — schools / voksenopplæring etc.).
const DEFAULT_VAT_RATE = 0.15;

// The ordinary Norwegian VAT rate. Applies to rent, service and other non-food
// charges, and does NOT vary by customer the way the food rate does.
const STANDARD_VAT_RATE = 0.25;

// VAT has TWO independent axes and this map is the first: what the LINE is.
//   'customer' — the kundekort's own rate. Food sales: 15% normally, 0 for a
//                VAT-exempt customer (schools, voksenopplæring).
//   'standard' — ordinary 25%. Rent/service are not food, so the customer's food
//                rate must not be applied to them (a real issued invoice, Faktura
//                13618, charges "Leie friggkantine per måned" at 25%).
//   'exempt'   — never taxed. Pant is a deposit, not a taxable sale.
// Collapsing these into one per-customer rate is what previously made pant need a
// bespoke `vatExemptTotal` branch; every new line kind belongs here instead.
//
// NOTE: garanti and drift are deliberately left on 'customer' — changing either is
// an accounting decision, not a code one. Drift in particular is an operating-cost
// fee rather than a food sale, so 25% may well be correct; confirm before moving it.
const VAT_BASIS_BY_KIND: { [kind: string]: 'customer' | 'standard' | 'exempt' } = {
  subsidy: 'customer',
  credit: 'customer',
  motemat: 'customer',
  // Coffee per cup from the Vendon machines — food/drink, customer's rate (15%),
  // as on the hand invoices ("Subsidier Frigg kantine Kaffe ...").
  kaffe: 'customer',
  // Juice per cup, entered on the Juice tab — a drink sale, customer's rate.
  juice: 'customer',
  garanti: 'customer',
  // 25%, confirmed by issued invoice 13591 (Asker Kommune), where this appears as
  // "Administrasjonsgebyr" 59,00 at 25% MVA. It is an administration fee, not a
  // food sale, so the customer's food rate never applied to it.
  drift: 'standard',
  // Minus line on the garanti customer's invoice. PowerOffice product 26 books it
  // to 3708 "Kickback inntekt garantiomsetning", VAT code 3 = 25% (checked
  // 2026-11-02), and the client chose to keep 25%.
  kickback: 'standard',
  pant: 'exempt',
  leie: 'standard',
  driftstotte: 'standard',
  service: 'standard',
  annet: 'standard'
};

// The VAT rate (decimal) one invoice line is billed at — the same rule the
// invoice totals use. Sent per line to the PowerOffice preflight, which compares
// it with the rate Go would actually apply.
function lineVatRate(inv: any, kind: string): number {
  const basis = VAT_BASIS_BY_KIND[kind] || 'customer';
  return basis === 'standard' ? STANDARD_VAT_RATE : (basis === 'exempt' ? 0 : inv.vatRate);
}

// Resolve a customer's VAT rate (decimal, e.g. 0.15 or 0). Accepts a rate stored
// either as a decimal (0.15) or as whole percent (15) on the kundekort.
function customerVatRate(cust: any): number {
  const v = cust ? cust.vatRate : null;
  if (v == null || !isFinite(v)) { return DEFAULT_VAT_RATE; }
  const n = Number(v);
  return n > 1 ? n / 100 : n;   // 15 -> 0.15, 0.15 -> 0.15, 0 -> 0
}

// Compute the invoice amounts for a customer. The two inputs have DIFFERENT VAT
// bases: `confirmedSubsidyExVAT` is ex-VAT (VAT must be ADDED), while
// `creditConsumed` is a VAT-INCLUSIVE retail amount (the price the member paid
// from credit — VAT already baked in, so it must be BACKED OUT, not added again).
// Returns ex-VAT subtotal, total VAT, and the gross to bill.
function invoiceAmounts(confirmedSubsidyExVAT: number, creditConsumed: number, rate: number) {
  const subExVat = Number(confirmedSubsidyExVAT) || 0;
  const subVat = subExVat * rate;
  const creditGross = Number(creditConsumed) || 0;
  const creditExVat = rate > 0 ? creditGross / (1 + rate) : creditGross;
  const creditVat = creditGross - creditExVat;
  const exVat = subExVat + creditExVat;
  const vat = subVat + creditVat;
  return {
    subExVat, subVat,
    creditExVat, creditVat,
    exVat: +exVat.toFixed(2),
    vat: +vat.toFixed(2),
    gross: +(exVat + vat).toFixed(2)
  };
}

// Seller (Frigg) details printed on every invoice. TODO: replace the placeholders
// with the real legal name / org number / address / bank account + KID before any
// invoice is actually sent. The reconciliation + totals are correct regardless;
// only these header fields need filling in.
// Seller (issuer) shown on the printed invoice. Name/org/address verified
// against Brønnøysundregistrene (data.brreg.no) for org 997435192 on
// 2026-08-07. NOTE: the company was renamed CUBOID AS -> FRIGG TECHNOLOGIES AS
// on 2026-07-27 (same legal entity, same org number) — the invoice must carry
// the CURRENT registered name, so "Cuboid AS" is deliberately not used here.
// Registered in Merverdiavgiftsregisteret, hence the MVA suffix on the org id.
const SELLER = {
  name: 'Frigg Technologies AS',
  orgId: 'NO 997 435 192 MVA',
  address: 'Øvre Eikervei 177',
  zipCity: '3048 Drammen',
  // Taken from a real issued invoice (Faktura 13618 to Asker Kommune, 01.09.2026),
  // which is authoritative for what customers already see. Note it still carries
  // the cuboid.no addresses despite the CUBOID AS -> FRIGG TECHNOLOGIES AS rename:
  // change these when the domain actually migrates, not before, or payment
  // references stop matching what customers have on file.
  email: 'faktura@cuboid.no',
  phone: '32833332',
  bankAccount: '1503 24 15196',
  // "Vår referanse" — the Frigg person handling the account. The same name on
  // every invoice today, so it is a default rather than data on 154 kundekort;
  // a kundekort that sets its own ourReference still wins. Taken from the
  // hand-made Asker invoices, which is what customers already see.
  ourReference: 'Tine H. R. Jönsson',
};

// ---- PowerOffice Go "Ordreimport" file --------------------------------------
// The same invoices as the API path, as PowerOffice's standard import file that
// an operator uploads by hand (Go: Regnskap → Import → Standardimport). It makes
// the same DRAFT orders the API makes, so it stands in for the API while
// production keys are pending. Two things the API path has that this does not:
// the attached transaction specification (attach it by hand on each draft from
// the "Transaction specs" zip), and the duplicate guard — Go has no external
// reference on an imported order, so importing the same file twice makes every
// draft twice.
//
// File description: hjelpesenter.poweroffice.no/ordreimport-filoppsett
// (ordreimport-filbeskrivelse.pdf, ordreimport-eksempelfil-oppsett.xlsx).
//
// Product NUMBERS from the client's own PowerOffice register — the same ones
// functions/.env (POWEROFFICE_PRODUCT_CODE_*) gives the API path; keep the two
// in step. VAT is not on the line: it comes from the product's sales account,
// with the customer's IsVatFree flag switching to the alternative VAT-free
// account — exactly as for the API. Coffee and juice ride on the subsidy
// product, as the API does while they have no product of their own.
const PO_PRODUCT_NO_BY_KIND: { [kind: string]: string } = {
  subsidy: '15', credit: '15', kaffe: '15', juice: '15',
  garanti: '25', kickback: '26', pant: '29', motemat: '30', service: '38',
  drift: '2', leie: '4', driftstotte: '6'
  // annet: nothing in the register fits yet — a line of that kind stops the export,
  // as it stops the API send, rather than posting to a guessed account.
};
// "Vår referanse" = an employee NUMBER in Go, not free text (see the .env note).
const PO_SALESPERSON_EMPLOYEE_NO = 10;
// Every column of PowerOffice's own example file, in its order, so the importer
// meets exactly the template it documents. Only a handful are filled.
const PO_ORDER_COLUMNS = [
  'OrderNo', 'OrderDate', 'RecurringInvoiceActive', 'RecurringInvoiceRepeatTimes', 'RecurringInvoiceEndDate',
  'RecurringInvoiceSendMethod', 'RecurringInvoiceSendFrequency', 'RecurringInvoiceSendFrequencyUnit',
  'NextRecurringInvoiceDate', 'SalesPersonEmployeeNo', 'SalesPersonName', 'ProjectCode', 'SubprojectCode',
  'ProjectName', 'ProjectManagerCode', 'ProjectManagerName', 'ProjectBillable', 'ProjectStartDate',
  'ProjectEndDate', 'ProjectStatus', 'ProjectContactPerson', 'DepartmentCode', 'DepartmentName',
  'DepartmentManagerCode', 'DepartmentManagerName', 'CustomerNo', 'ContactName', 'ContactGroup',
  'CustomerSince', 'IsVatFree', 'Phone', 'Email', 'Web', 'OrganizationNo', 'MailAddress1', 'MailAddress2',
  'MailPostcode', 'MailCity', 'MailCountry', 'DeliveryAddress1', 'DeliveryAddress2', 'DeliveryPostcode',
  'DeliveryCity', 'DeliveryCountry', 'BankAccount', 'IBAN', 'SWIFT', 'InvoiceDelivery',
  'ContactPersonFirstName', 'ContactPersonLastName', 'ContactPersonPhone', 'ContactPersonEmail',
  'Reference', 'PaymentTerms', 'MergeWithPreviousOrder', 'Currency', 'ProductCode', 'ProductName',
  'ProductGroup', 'ProductDescription', 'ProductType', 'ProductUnit', 'ProductSalesPrice',
  'ProductCostPrice', 'ProductSalesAccount', 'ProductSalesAccountName', 'ProductAltSalesAccount',
  'ProductAltSalesAccountName', 'ProductGTIN', 'Discount', 'Quantity', 'Description', 'OrderLineUnitPrice',
  'SortOrder', 'VATReturnSpecification'
];

// Global (company-wide) billing dashboard: the monthly subsidy invoice-report
// estimate, settlement reconciliation against the Vipps/Stripe exports, and
// per-customer invoice generation. Company-scoped via MembershipV2Service (the
// reconcile / invoiceReport endpoints key on companyId), so it isn't tied to any
// selected site — it lives as a top-level page.
@Component({
  selector: 'v2-billing',
  templateUrl: './billing.component.html',
  styleUrls: ['../../shared/styles/crm-billing.css', './billing.component.css']
})
export class BillingComponent implements OnInit, OnDestroy {

  @ViewChild(GarantiomsetningComponent) garantiomsetningCmp?: GarantiomsetningComponent;

  customers: any[] = [];

  // Defaults to LAST month — the month being invoiced. Picked from a dropdown
  // (monthOptions); the month is the single source of truth for everything the
  // invoice is built from, see onInvoiceMonthChange() and buildInvoices().
  invoiceMonth = moment().subtract(1, 'month').format('YYYY-MM');
  readonly monthOptions: { value: string, label: string }[] = (() => {
    const out: { value: string, label: string }[] = [];
    const m = moment().add(1, 'month').startOf('month');
    const stop = moment('2025-01', 'YYYY-MM');
    while (!m.isBefore(stop)) {
      out.push({ value: m.format('YYYY-MM'), label: m.clone().locale('en').format('MMMM YYYY') });
      m.subtract(1, 'month');
    }
    return out;
  })();
  // The month the reconciliation currently on screen belongs to.
  reconMonth = '';

  // settlement reconciliation
  vippsFileName = '';
  stripeFileName = '';
  private vippsRows: any[] | null = null;
  private stripeRows: any[] | null = null;
  private vippsFile: File | null = null;      // raw file, kept for the Storage archive
  private stripeFile: File | null = null;
  reconLoading = false;
  reconResult: any = null;
  reconError = '';
  generatingInvoices = false;
  exportingInvoices = false;
  exportingSpecs = false;
  // One row per billed transaction behind reconResult (reconcileSettlements
  // with includeLines) — the specification attached to each invoice. Not bound
  // to the template. Loaded lazily for a saved avstemming, see reconRows().
  private reconLines: { columns: string[], rows: any[][] } | null = null;
  private savedHasTransactions = false;
  sendingToPowerOffice = false;
  poSendProgress = '';
  powerOfficeResults: any[] | null = null;
  powerOfficeError = '';
  exportingPowerOfficeFile = false;
  // The checked batch waiting for the operator's go-ahead: what the preflight
  // said about each invoice, and the exact invoice objects it checked — those,
  // not a rebuilt set, are what gets sent.
  checkingPowerOffice = false;
  poCheck: {
    environment: string, client: any, rows: any[],
    invoices: { [ref: string]: any }, month: string
  } | null = null;
  // What the last import file contained, shown under the toolbar.
  powerOfficeFileNote = '';
  // Customers the last invoice build left out because their kundekort is not Active.
  notActiveNote = '';
  showFlagged = false;
  showUnassigned = false;
  expandedCustomer: { [customerId: string]: boolean } = {};

  // 0 = Reconcile & Invoice, 1 = Møtemat, 2 = Garantiomsetning, 3 = Driftskostnader, 4 = Avtaler, 5 = Juice, 6 = History
  selectedTabIndex = 0;

  // møtemat ordered in the customer portal and approved — folded into the
  // invoices, and previewed below so the operator sees it before generating.
  // A FIELD, not a getter: it is bound to *ngFor and must not be rebuilt on
  // every change-detection pass.
  motematRows: { customerId: string, name: string, orderCount: number, grossIncVat: number }[] = [];
  motematTotal = 0;
  motematLoading = false;

  // Driftskostnader (operating cost) preview — see driftkostnad.service.ts.
  // Not month-scoped server-side (reflects today's system links), reloaded on
  // month change anyway for a consistent "refresh everything" toolbar.
  driftRows: { customerId: string, name: string, systemCount: number, billable: number }[] = [];
  driftTotal = 0;
  driftLoading = false;
  readonly DRIFTKOSTNAD_RATE_EX_VAT = DRIFTKOSTNAD_RATE_EX_VAT;

  // ---- Avtalelinjer (recurring per-system contract charges) ----------------
  // Rows are FIELDS rebuilt by recompute(), never getters: a getter that mints new
  // objects on every change-detection pass and is bound to *ngFor froze the
  // Customer Mapping page once already (see customer-mapping.component.ts).
  readonly AVTALE_KINDS = AVTALE_KINDS;

  // ---- Juice (manual, per system per month) --------------------------------
  // BIB dispensers report nothing, so juice is entered here — see juice.service.ts.
  readonly JUICE_TYPES = JUICE_TYPES;
  juiceRows: any[] = [];
  juiceTotal = 0;
  juiceError = '';
  juiceSaving = false;
  private juiceSub: Subscription | null = null;
  private juiceEntries: JuiceEntry[] = [];
  newJuice: any = { systemId: '', juiceType: 'appelsin', bibs: null, cups: null, pricePerCupExVat: JUICE_DEFAULT_PRICE_EX_VAT, note: '' };
  avtalelinjer: Avtalelinje[] = [];
  avtaleRows: any[] = [];
  avtaleSystems: { systemId: string, name: string, owner: string }[] = [];
  avtaleTotal = 0;
  avtaleSaving: { [id: string]: boolean } = {};
  avtaleError = '';
  newAvtale: any = { systemId: '', kind: 'leie', description: '', amountExVat: null, interval: 'monthly', startMonth: '', endMonth: '' };

  // avstemming persistence
  avstemminger: any[] = [];                    // saved months (history)
  savingAvstemming = false;
  savedNote = '';
  viewingSaved = false;                        // true when reconResult came from a saved doc
  deletingAvstemming = false;

  private subs: Subscription[] = [];

  _isSuperAdmin = false;
  // Super-admin only: fan out reconcile across every company. On by default —
  // billing is always done for all companies at once.
  showAllCompanies = true;
  companiesMap: { [id: string]: string } = {};

  constructor(
    private svc: MembershipV2Service,
    private kundeKort: KundeKortService,
    private avstemming: AvstemmingService,
    private auth: AuthService,
    private garantiSvc: GarantiomsetningService,
    private motematSvc: MotematBillingService,
    private driftSvc: DriftkostnadService,
    private avtaleSvc: AvtalelinjeService,
    private juiceSvc: JuiceService,
    private systemRefSvc: SystemReferenceService,
    private config: ConfigService
  ) { }

  ngOnInit() {
    this.subs.push(this.kundeKort.getCustomers().subscribe((c: any[]) => this.customers = c));
    this.subs.push(this.auth.user$.subscribe(u => this.adminEmail = u ? u.email : ''));
    this.subs.push(this.avstemming.list$().subscribe(list => {
      this.avstemminger = list;
      // First load: show the selected month's saved avstemming, if there is one.
      if (!this.reconResult && !this.reconLoading) { this.loadSavedForMonth(); }
    }));
    this.subs.push(this.auth.getUser$().subscribe(u => { this._isSuperAdmin = this.auth.isSuperAdmin(u); }));
    this.svc.getCompaniesMapOnce().then(m => { this.companiesMap = m; }).catch(() => { });
    this.loadMotemat();
    this.loadDrift();

    this.subs.push(this.avtaleSvc.list$().subscribe(list => { this.avtalelinjer = list; this.recomputeAvtale(); }));
    this.loadJuice();
    this.subs.push(this.kundeKort.getAllSystems$().subscribe((systems: any[]) => {
      this.kundeKort.getAllSystemKunder$().subscribe((links: any[]) => {
        const byDoc: { [id: string]: string } = {};
        links.forEach(l => { if (l.systemId && l.kundekortId) { byDoc[String(l.systemId)] = l.kundekortId; } });
        this.avtaleSystems = (systems || [])
          .filter(sy => sy.active !== false)
          .map(sy => {
            const key = String(sy.systemId || '');
            const owner = byDoc[key] || byDoc[String((sy as any).dbid || '')] || null;
            return { systemId: key, name: sy.name || key, owner: owner ? this.customerName(owner) : '(no customer linked)' };
          })
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nb'));
        this.recomputeAvtale();
        this.recomputeJuice();
      });
    }));
  }

  // Month-scoped: re-subscribed whenever the invoice month changes.
  loadJuice() {
    if (this.juiceSub) { this.juiceSub.unsubscribe(); }
    this.juiceEntries = []; this.recomputeJuice();
    if (!this.invoiceMonth) { return; }
    this.juiceSvc.getExplicitPayers().then(p => { this.juicePayers = p; this.recomputeJuice(); }).catch(() => { });
    this.juiceSub = this.juiceSvc.listForMonth$(this.invoiceMonth).subscribe(list => { this.juiceEntries = list; this.recomputeJuice(); });
  }

  // systemId -> the one customer billed for that system's purchases in the
  // reconciliation on screen. Systems with no purchases, or purchases billed to
  // several customers, are left out. Juice follows this (see buildInvoices).
  private juicePayers: { [systemId: string]: string } = {};

  // Who gets a system's juice: its explicit juice payer (System → juiceKundekortId)
  // when set, else the customer billed for its purchases.
  private juiceCustomerBySystem(explicit = this.juicePayers): { [systemId: string]: string } {
    return { ...this.purchaseCustomerBySystem(), ...explicit };
  }

  private purchaseCustomerBySystem(): { [systemId: string]: string } {
    const buyers: { [systemId: string]: Set<string> } = {};
    (this.reconResult?.customers || []).forEach((c: any) => {
      if (c.customerId === 'UNASSIGNED') { return; }
      (c.systems || []).forEach((s: any) => {
        if (s.systemId && s.billableTotal > 0) { (buyers[s.systemId] || (buyers[s.systemId] = new Set())).add(c.customerId); }
      });
    });
    const out: { [systemId: string]: string } = {};
    Object.keys(buyers).forEach(id => { if (buyers[id].size === 1) { out[id] = Array.from(buyers[id])[0]; } });
    return out;
  }

  recomputeJuice() {
    const purchaseCustomer = this.juiceCustomerBySystem();
    const sysById: { [id: string]: any } = {};
    this.avtaleSystems.forEach(sy => { sysById[sy.systemId] = sy; });
    this.juiceRows = (this.juiceEntries || []).map(e => {
      const sy = sysById[String(e.systemId)];
      const t = JUICE_TYPES.find(x => x.key === e.juiceType);
      return {
        ...e,
        systemName: sy ? sy.name : `${e.systemId} (unknown system)`,
        // The customer the invoice will actually use (see buildInvoices).
        owner: purchaseCustomer[String(e.systemId)] ? this.customerName(purchaseCustomer[String(e.systemId)]) : (sy ? sy.owner : '—'),
        typeLabel: t ? t.label : e.juiceType,
        amountExVat: +((Number(e.cups) || 0) * (Number(e.pricePerCupExVat) || 0)).toFixed(2)
      };
    }).sort((a, b) => (a.systemName || '').localeCompare(b.systemName || '', 'nb'));
    this.juiceTotal = +this.juiceRows.reduce((n, r) => n + r.amountExVat, 0).toFixed(2);
  }

  // Cups follow from bibs × cups-per-bib; "cups counted directly" takes cups as typed.
  onJuiceBibsChange() {
    const t = JUICE_TYPES.find(x => x.key === this.newJuice.juiceType);
    if (t && t.cupsPerBib && this.newJuice.bibs != null && this.newJuice.bibs !== '') {
      this.newJuice.cups = Math.round(Number(this.newJuice.bibs) * t.cupsPerBib);
    }
  }

  async addJuice() {
    this.juiceError = '';
    const j = this.newJuice;
    const t = JUICE_TYPES.find(x => x.key === j.juiceType);
    if (!this.invoiceMonth) { this.juiceError = 'Pick the invoice month at the top first.'; return; }
    if (!j.systemId) { this.juiceError = 'Pick a system.'; return; }
    if (t && t.cupsPerBib && !(Number(j.bibs) > 0)) { this.juiceError = 'Enter how many BIBs were used.'; return; }
    if (!(Number(j.cups) > 0) || !Number.isInteger(Number(j.cups))) { this.juiceError = 'Cups must be a whole number above 0.'; return; }
    if (!(Number(j.pricePerCupExVat) > 0)) { this.juiceError = 'Price per cup must be greater than 0.'; return; }
    const sy = this.avtaleSystems.find(x => x.systemId === j.systemId);
    if (sy && sy.owner === '(no customer linked)') { this.juiceError = 'This system has no customer linked — link it to a kundekort first.'; return; }
    this.juiceSaving = true;
    try {
      await this.juiceSvc.add({
        month: this.invoiceMonth,
        systemId: String(j.systemId),
        juiceType: j.juiceType,
        bibs: t && t.cupsPerBib ? Number(j.bibs) : null,
        cups: Number(j.cups),
        pricePerCupExVat: +Number(j.pricePerCupExVat).toFixed(2),
        note: (j.note || '').trim(),
        createdBy: this.adminEmail || ''
      });
      this.newJuice = { systemId: j.systemId, juiceType: j.juiceType, bibs: null, cups: null, pricePerCupExVat: j.pricePerCupExVat, note: '' };
    } catch (err: any) {
      this.juiceError = 'Could not save: ' + ((err && err.message) || 'unknown error');
    }
    this.juiceSaving = false;
  }

  async removeJuice(row: any) {
    if (!confirm(`Delete ${row.cups} cups of ${row.typeLabel} on ${row.systemName}?`)) { return; }
    try { await this.juiceSvc.remove(row.dbid); }
    catch (err: any) { this.juiceError = 'Could not delete: ' + ((err && err.message) || 'unknown error'); }
  }

  // Joins the stored agreements with system + owner names, and marks which fall
  // due in the month currently selected at the top of the page.
  recomputeAvtale() {
    const sysById: { [id: string]: any } = {};
    this.avtaleSystems.forEach(sy => { sysById[sy.systemId] = sy; });
    this.avtaleRows = (this.avtalelinjer || []).map(l => {
      const sy = sysById[String(l.systemId)];
      return {
        ...l,
        systemName: sy ? sy.name : `${l.systemId} (unknown system)`,
        owner: sy ? sy.owner : '—',
        due: AvtalelinjeService.dueInMonth(l, this.invoiceMonth)
      };
    }).sort((a, b) => (a.systemName || '').localeCompare(b.systemName || '', 'nb') || (a.kind || '').localeCompare(b.kind || ''));
    this.avtaleTotal = +this.avtaleRows.filter(r => r.due)
      .reduce((n, r) => n + (Number(r.amountExVat) || 0), 0).toFixed(2);
  }

  onAvtaleKindChange() {
    const k = AVTALE_KINDS.find(x => x.kind === this.newAvtale.kind);
    if (k && !this.newAvtale.description) { this.newAvtale.description = k.defaultDescription; }
  }

  async addAvtale() {
    this.avtaleError = '';
    const a = this.newAvtale;
    if (!a.systemId) { this.avtaleError = 'Pick a system.'; return; }
    if (!(Number(a.amountExVat) > 0)) { this.avtaleError = 'Amount must be greater than 0.'; return; }
    if (!a.startMonth) { this.avtaleError = 'Set the month this agreement starts.'; return; }
    const kind = AVTALE_KINDS.find(x => x.kind === a.kind);
    this.avtaleSaving['new'] = true;
    try {
      await this.avtaleSvc.add({
        systemId: String(a.systemId),
        kind: a.kind,
        description: (a.description || (kind ? kind.defaultDescription : '')).trim(),
        amountExVat: +Number(a.amountExVat).toFixed(2),
        interval: a.interval || 'monthly',
        startMonth: a.startMonth,
        endMonth: a.endMonth || null,
        active: true
      });
      this.newAvtale = { systemId: '', kind: 'leie', description: '', amountExVat: null, interval: 'monthly', startMonth: '', endMonth: '' };
    } catch (err: any) {
      this.avtaleError = 'Could not save: ' + ((err && err.message) || 'unknown error');
    }
    this.avtaleSaving['new'] = false;
  }

  async toggleAvtale(row: any) {
    this.avtaleSaving[row.dbid] = true;
    try { await this.avtaleSvc.update(row.dbid, { active: !row.active }); }
    catch (err: any) { this.avtaleError = 'Could not update: ' + ((err && err.message) || 'unknown error'); }
    this.avtaleSaving[row.dbid] = false;
  }

  async removeAvtale(row: any) {
    if (!confirm(`Delete "${row.description}" (${row.amountExVat} kr) on ${row.systemName}?`)) { return; }
    this.avtaleSaving[row.dbid] = true;
    try { await this.avtaleSvc.remove(row.dbid); }
    catch (err: any) { this.avtaleError = 'Could not delete: ' + ((err && err.message) || 'unknown error'); }
    this.avtaleSaving[row.dbid] = false;
  }

  // Puts the selected month's saved avstemming on screen, or clears a
  // reconciliation that belongs to another month — invoices must never combine
  // one month's consumption with another month's coffee, rent or garanti.
  private loadSavedForMonth() {
    const saved = (this.avstemminger || []).find(a => a.period && a.period.month === this.invoiceMonth);
    if (saved) { this.openAvstemming(saved, false); return; }
    if (this.reconResult && this.reconMonth !== this.invoiceMonth) {
      this.reconResult = null; this.reconLines = null; this.viewingSaved = false; this.reconMonth = '';
      this.savedNote = `No saved avstemming for ${this.invoiceMonth} — upload the settlement files and run it.`;
    }
  }

  onInvoiceMonthChange() {
    this.loadSavedForMonth();
    this.loadMotemat();
    this.loadDrift();
    this.recomputeAvtale();   // `due` is month-dependent
    this.loadJuice();
  }

  // Which companies the billing page is currently working across.
  private billingCompanyIds(): string[] {
    return (this.showAllCompanies && this._isSuperAdmin)
      ? Object.keys(this.companiesMap)
      : [this.config.getCompanyId()];
  }

  async loadMotemat() {
    if (!this.invoiceMonth) { return; }
    this.motematLoading = true;
    try {
      const byCustomer = await this.motematSvc.getBillableByCustomer(this.invoiceMonth, this.billingCompanyIds());
      this.motematRows = Object.keys(byCustomer).map(cid => ({
        customerId: cid,
        name: this.customerName(cid),
        orderCount: byCustomer[cid].orders.length,
        grossIncVat: byCustomer[cid].grossIncVat
      })).sort((a, b) => b.grossIncVat - a.grossIncVat);
      this.motematTotal = +this.motematRows.reduce((s, r) => s + r.grossIncVat, 0).toFixed(2);
    } catch (err) {
      // Never block the page on this — it is a preview of a line the invoice
      // builder computes independently.
      this.motematRows = [];
      this.motematTotal = 0;
    }
    this.motematLoading = false;
  }

  async loadDrift() {
    this.driftLoading = true;
    try {
      const byCustomer = await this.driftSvc.getBillableByCustomer();
      this.driftRows = Object.keys(byCustomer).map(cid => ({
        customerId: cid,
        name: this.customerName(cid),
        systemCount: byCustomer[cid].systemCount,
        billable: byCustomer[cid].billable
      })).sort((a, b) => b.billable - a.billable);
      this.driftTotal = +this.driftRows.reduce((s, r) => s + r.billable, 0).toFixed(2);
    } catch (err) {
      this.driftRows = [];
      this.driftTotal = 0;
    }
    this.driftLoading = false;
  }

  adminEmail = '';

  companyName(companyId: string) {
    return this.companiesMap[companyId] || (companyId || '—');
  }

  ngOnDestroy() { this.subs.forEach(s => s.unsubscribe()); if (this.juiceSub) { this.juiceSub.unsubscribe(); } }

  customerName(id: string) {
    const c = this.customers.find(x => x.kundekortId === id);
    return c ? c.name : (id ? '(unknown)' : '—');
  }

  // ---- settlement reconciliation -----------------------------------------
  async onVippsFile(e: Event) {
    const f = (e.target as HTMLInputElement).files ? (e.target as HTMLInputElement).files[0] : null;
    if (!f) { return; }
    this.vippsFileName = f.name; this.vippsFile = f; this.reconError = ''; this.reconResult = null; this.reconLines = null; this.savedNote = '';
    try { this.vippsRows = await this.parseVipps(f); }
    catch (err) { this.vippsRows = null; this.vippsFile = null; this.reconError = 'Could not read the Vipps file (expected the settlement report .csv).'; }
  }

  async onStripeFile(e: Event) {
    const f = (e.target as HTMLInputElement).files ? (e.target as HTMLInputElement).files[0] : null;
    if (!f) { return; }
    this.stripeFileName = f.name; this.stripeFile = f; this.reconError = ''; this.reconResult = null; this.reconLines = null; this.savedNote = '';
    try { this.stripeRows = await this.parseStripe(f); }
    catch (err) { this.stripeRows = null; this.stripeFile = null; this.reconError = 'Could not read the Stripe file (expected the exported .csv).'; }
  }

  // Nothing uploaded this session: reuse the files archived with the month's
  // saved avstemming, so a month can be re-run after a restamp or a group move
  // without hunting down the reports again.
  private async loadArchivedSettlements(): Promise<void> {
    const saved = (this.avstemminger || []).find(a => a.period && a.period.month === this.invoiceMonth);
    const meta = (saved && saved.settlementMeta) || {};
    if (meta.vipps && meta.vipps.path) {
      this.vippsFile = await this.avstemming.downloadFile(meta.vipps.path, meta.vipps.fileName);
      this.vippsFileName = meta.vipps.fileName;
      this.vippsRows = await this.parseVipps(this.vippsFile);
    }
    if (meta.stripe && meta.stripe.path) {
      this.stripeFile = await this.avstemming.downloadFile(meta.stripe.path, meta.stripe.fileName);
      this.stripeFileName = meta.stripe.fileName;
      this.stripeRows = await this.parseStripe(this.stripeFile);
    }
  }

  async runReconcile() {
    if (!this.invoiceMonth) { return; }
    if (!this.vippsRows && !this.stripeRows) {
      this.reconLoading = true; this.reconError = '';
      try { await this.loadArchivedSettlements(); }
      catch (err) {
        console.error('loadArchivedSettlements failed', err);
        this.vippsFile = null; this.stripeFile = null; this.vippsRows = null; this.stripeRows = null;
      }
      this.reconLoading = false;
    }
    if (!this.vippsRows && !this.stripeRows) {
      this.reconError = 'Upload at least one settlement file (Vipps and/or Stripe).';
      return;
    }
    const from = moment(this.invoiceMonth, 'YYYY-MM').startOf('month').unix();
    const to = moment(this.invoiceMonth, 'YYYY-MM').endOf('month').unix();
    const settlements = [...(this.vippsRows || []), ...(this.stripeRows || [])];
    this.reconLoading = true; this.reconResult = null; this.reconLines = null; this.reconError = ''; this.savedNote = ''; this.viewingSaved = false;
    this.reconMonth = this.invoiceMonth;

    if (this.showAllCompanies && this._isSuperAdmin) {
      const companyIds = Object.keys(this.companiesMap);
      // Same uploaded settlement file (one shared Vipps/Stripe merchant account
      // across companies) matched against each company's own transactions.
      Promise.all(companyIds.map(cid =>
        this.svc.reconcileSettlements(from, to, settlements, cid).toPromise().catch(() => ({ success: false }))
      )).then((results: any[]) => {
        this.reconLoading = false;
        const ok = results.filter(r => r && r.success !== false);
        if (!ok.length) { this.reconError = 'Reconciliation failed.'; return; }
        this.reconResult = this.mergeReconcileResults(ok);
        this.recomputeJuice();
        this.reconLines = {
          columns: ok[0].lineColumns || [],
          rows: ([] as any[][]).concat(...ok.map(r => r.lines || []))
        };
        this.garantiomsetningCmp?.recomputeAll(true);
      });
      return;
    }

    this.svc.reconcileSettlements(from, to, settlements).subscribe((res: any) => {
      this.reconLoading = false;
      this.reconLines = { columns: res.lineColumns || [], rows: res.lines || [] };
      delete res.lines;
      this.reconResult = res;
      this.recomputeJuice();
      this.garantiomsetningCmp?.recomputeAll(true);
    }, () => {
      this.reconLoading = false; this.reconError = 'Reconciliation failed.';
    });
  }

  // Merges per-company reconcileSettlements() results into one combined view.
  // settlementOrders/settlementVipps/settlementStripe describe the UPLOADED FILE
  // itself (identical across every company response, since it's the same file
  // matched N times) — taken from the first result, not summed. Everything else
  // is derived from each company's own transactions and is summed/concatenated.
  private mergeReconcileResults(results: any[]): any {
    const SUMMED_SUMMARY_KEYS = [
      'transactions', 'card', 'credit', 'confirmed', 'unmatched', 'unmatchedVipps',
      'unmatchedStripe', 'noOrderId', 'refunded', 'partialRefund',
      'derivedCustomer', 'derivedCustomerSystem', 'stillUnassigned', 'zeroSubsidyCard',
      'invoiceBySystemMoved', 'invoiceBySystemAmbiguous'
    ];
    const summary: any = {};
    SUMMED_SUMMARY_KEYS.forEach(k => { summary[k] = results.reduce((s, r) => s + (Number(r.summary?.[k]) || 0), 0); });
    summary.settlementOrders = results[0].summary?.settlementOrders || 0;
    summary.settlementVipps = results[0].summary?.settlementVipps || 0;
    summary.settlementStripe = results[0].summary?.settlementStripe || 0;

    const byCustomer: { [cid: string]: any } = {};
    results.forEach(r => (r.customers || []).forEach((c: any) => {
      const b = byCustomer[c.customerId] || (byCustomer[c.customerId] = {
        customerId: c.customerId, confirmedSubsidyExVAT: 0, creditConsumed: 0, pantTotal: 0, creditPantTotal: 0,
        billableTotal: 0, confirmedCount: 0, creditCount: 0, flaggedCount: 0, groups: {}, systems: {}
      });
      b.confirmedSubsidyExVAT += Number(c.confirmedSubsidyExVAT) || 0;
      b.creditConsumed += Number(c.creditConsumed) || 0;
      b.pantTotal += Number(c.pantTotal) || 0;
      // Pant PAID WITH CREDIT is the billable part — plain pantTotal includes
      // deposits paid by card, which the customer never owes. buildInvoices()
      // reads c.creditPantTotal for the VAT-free pant line, and this merge was
      // not accumulating it, so the line silently vanished for every customer
      // that does not split: Sparebanken Øst's 366 kr of July pant among them.
      b.creditPantTotal += Number(c.creditPantTotal) || 0;
      b.billableTotal += Number(c.billableTotal) || 0;
      b.confirmedCount += Number(c.confirmedCount) || 0;
      b.creditCount += Number(c.creditCount) || 0;
      b.flaggedCount += Number(c.flaggedCount) || 0;
      // Per-group breakdown of this same customer (see reconcileSettlements.js) —
      // merged the same way across companies as the customer totals above.
      (c.groups || []).forEach((g: any) => {
        const key = g.groupId || 'NO_GROUP';
        const gb = b.groups[key] || (b.groups[key] = {
          groupId: g.groupId, groupName: g.groupName, confirmedSubsidyExVAT: 0, creditConsumed: 0, pantTotal: 0,
          creditPantTotal: 0, billableTotal: 0, confirmedCount: 0, creditCount: 0
        });
        gb.confirmedSubsidyExVAT += Number(g.confirmedSubsidyExVAT) || 0;
        gb.creditConsumed += Number(g.creditConsumed) || 0;
        gb.pantTotal += Number(g.pantTotal) || 0;
        gb.creditPantTotal += Number(g.creditPantTotal) || 0;
        gb.billableTotal += Number(g.billableTotal) || 0;
        gb.confirmedCount += Number(g.confirmedCount) || 0;
        gb.creditCount += Number(g.creditCount) || 0;
      });
      // Per-SYSTEM breakdown, merged the same way. This is what split billing
      // slices by — the split key is the physical system a purchase happened at,
      // not the buyer's group. Dropping it here (as this did) left c.systems
      // undefined in buildInvoices(), so refSet never learned that a customer's
      // systems carry different references and every customer got ONE combined
      // invoice, however carefully the per-system references were set.
      (c.systems || []).forEach((sy: any) => {
        const key = sy.systemId || 'NO_SYSTEM';
        const sb = b.systems[key] || (b.systems[key] = {
          systemId: sy.systemId, systemName: sy.systemName, confirmedSubsidyExVAT: 0, creditConsumed: 0,
          pantTotal: 0, creditPantTotal: 0, billableTotal: 0, confirmedCount: 0, creditCount: 0
        });
        sb.confirmedSubsidyExVAT += Number(sy.confirmedSubsidyExVAT) || 0;
        sb.creditConsumed += Number(sy.creditConsumed) || 0;
        sb.pantTotal += Number(sy.pantTotal) || 0;
        sb.creditPantTotal += Number(sy.creditPantTotal) || 0;
        sb.billableTotal += Number(sy.billableTotal) || 0;
        sb.confirmedCount += Number(sy.confirmedCount) || 0;
        sb.creditCount += Number(sy.creditCount) || 0;
      });
    }));
    const customers = Object.values(byCustomer).map((c: any) => ({
      ...c,
      confirmedSubsidyExVAT: Number(c.confirmedSubsidyExVAT.toFixed(2)),
      creditConsumed: Number(c.creditConsumed.toFixed(2)),
      pantTotal: Number(c.pantTotal.toFixed(2)),
      creditPantTotal: Number(c.creditPantTotal.toFixed(2)),
      billableTotal: Number(c.billableTotal.toFixed(2)),
      groups: Object.values(c.groups).map((g: any) => ({
        ...g,
        confirmedSubsidyExVAT: Number(g.confirmedSubsidyExVAT.toFixed(2)),
        creditConsumed: Number(g.creditConsumed.toFixed(2)),
        pantTotal: Number(g.pantTotal.toFixed(2)),
        creditPantTotal: Number(g.creditPantTotal.toFixed(2)),
        billableTotal: Number(g.billableTotal.toFixed(2))
      })).sort((a: any, b: any) => b.billableTotal - a.billableTotal),
      systems: Object.values(c.systems).map((sy: any) => ({
        ...sy,
        confirmedSubsidyExVAT: Number(sy.confirmedSubsidyExVAT.toFixed(2)),
        creditConsumed: Number(sy.creditConsumed.toFixed(2)),
        pantTotal: Number(sy.pantTotal.toFixed(2)),
        creditPantTotal: Number(sy.creditPantTotal.toFixed(2)),
        billableTotal: Number(sy.billableTotal.toFixed(2))
      })).sort((a: any, b: any) => b.billableTotal - a.billableTotal)
    })).sort((a: any, b: any) => b.billableTotal - a.billableTotal);

    const byGroup: { [key: string]: any } = {};
    results.forEach(r => (r.unassignedBreakdown || []).forEach((u: any) => {
      const key = `${u.groupId || ''}|${u.systemId || ''}|${u.groupName || ''}`;
      const b = byGroup[key] || (byGroup[key] = { groupId: u.groupId, systemId: u.systemId, groupName: u.groupName, reason: u.reason, count: 0, billable: 0, flaggedCount: 0 });
      b.count += Number(u.count) || 0;
      b.billable += Number(u.billable) || 0;
      b.flaggedCount += Number(u.flaggedCount) || 0;
    }));
    const unassignedBreakdown = Object.values(byGroup)
      .map((u: any) => ({ ...u, billable: Number(u.billable.toFixed(2)) }))
      .sort((a: any, b: any) => (b.billable - a.billable) || (b.count - a.count));

    const flagged = ([] as any[]).concat(...results.map(r => r.flagged || []));
    const flaggedTotal = results.reduce((s, r) => s + (Number(r.flaggedTotal) || 0), 0);
    const flaggedTruncated = results.some(r => r.flaggedTruncated) || flaggedTotal > flagged.length;

    let grandSubsidy = 0, grandCredit = 0, grandPant = 0;
    results.forEach(r => { grandSubsidy += Number(r.grandSubsidy) || 0; grandCredit += Number(r.grandCredit) || 0; grandPant += Number(r.grandPant) || 0; });

    return {
      success: true,
      dateFrom: results[0].dateFrom, dateTo: results[0].dateTo,
      summary, customers, unassignedBreakdown,
      grandSubsidy: Number(grandSubsidy.toFixed(2)),
      grandCredit: Number(grandCredit.toFixed(2)),
      grandPant: Number(grandPant.toFixed(2)),   // reference only — NOT part of grandTotal
      grandTotal: Number((grandSubsidy + grandCredit).toFixed(2)),
      flaggedTotal, flaggedTruncated, flagged
    };
  }

  // ---- avstemming: save / open history -----------------------------------
  // Persist the current reconcile as the month's avstemming: archive the raw
  // settlement files to Storage and the compact result to Firestore.
  // Remove a month's saved avstemming so it can be run again. Needed after a
  // structural change — splitting a customer in two, moving a group — because
  // the snapshot keeps the old attribution and Save is hidden while viewing it.
  async deleteAvstemming() {
    if (!this.invoiceMonth) { return; }
    if (!confirm(
      `Delete the saved avstemming for ${this.invoiceMonth}?\n\n` +
      `The settlement files stay in storage. Note that re-running will NOT re-attribute ` +
      `transactions that were stamped with a customer at checkout — those keep the customer ` +
      `they were bought under.`)) { return; }
    this.deletingAvstemming = true;
    try {
      await this.avstemming.delete(this.invoiceMonth);
      this.reconResult = null;
      this.reconLines = null;
      this.viewingSaved = false;
      this.savedNote = `Saved avstemming for ${this.invoiceMonth} deleted. Upload the settlement files and run it again.`;
    } catch (err: any) {
      this.reconError = 'Could not delete: ' + ((err && err.message) || 'unknown error');
    }
    this.deletingAvstemming = false;
  }

  async saveAvstemming() {
    if (!this.reconResult || this.reconResult.success === false) { return; }
    if (this.viewingSaved) { return; }               // already a saved doc
    this.savingAvstemming = true; this.savedNote = ''; this.reconError = '';
    try {
      const month = this.invoiceMonth;
      const meta: any = { vipps: null, stripe: null };
      if (this.vippsFile) {
        const up = await this.avstemming.uploadFile(month, 'vipps', this.vippsFile);
        meta.vipps = { fileName: this.vippsFileName, orderCount: this.reconResult.summary.settlementVipps, ...up };
      }
      if (this.stripeFile) {
        const up = await this.avstemming.uploadFile(month, 'stripe', this.stripeFile);
        meta.stripe = { fileName: this.stripeFileName, orderCount: this.reconResult.summary.settlementStripe, ...up };
      }
      const r = this.reconResult;
      // The transaction rows go in first: an avstemming document that claims
      // rows it does not have would attach an empty specification.
      const transactions = this.reconLines && this.reconLines.rows.length
        ? await this.avstemming.saveTransactions(month, this.reconLines.columns, this.reconLines.rows)
        : null;
      await this.avstemming.save({
        transactions,
        period: {
          month,
          from: moment(month, 'YYYY-MM').startOf('month').unix(),
          to: moment(month, 'YYYY-MM').endOf('month').unix()
        },
        status: 'draft',
        createdBy: this.adminEmail || null,
        createdAt: Date.now(),
        settlementMeta: meta,
        summary: r.summary,
        customers: r.customers || [],
        unassignedBreakdown: r.unassignedBreakdown || [],
        flagged: r.flagged || [],
        flaggedTotal: r.flaggedTotal || 0,
        flaggedTruncated: r.flaggedTruncated || false,
        grandSubsidy: r.grandSubsidy, grandCredit: r.grandCredit, grandPant: r.grandPant || 0, grandTotal: r.grandTotal
      });
      this.viewingSaved = true;
      this.savedHasTransactions = !!transactions;
      this.savedNote = `Avstemming for ${month} saved.`;
    } catch (err) {
      console.error('saveAvstemming failed', err);
      const detail = (err && (err.code || err.message)) ? ` (${err.code || ''} ${err.message || ''})`.trim() : '';
      this.reconError = `Could not save the avstemming (file upload or write failed)${detail ? ' — ' + detail : ''}.`;
    } finally {
      this.savingAvstemming = false;
    }
  }

  // Load a saved month back into the reconcile view (read-only-ish; exports and
  // invoice generation work off reconResult so they keep working).
  openAvstemming(a: any, switchTab = true) {
    const month = a.period?.month || this.invoiceMonth;
    const monthChanged = month !== this.invoiceMonth;
    this.invoiceMonth = month;
    this.reconMonth = month;
    this.reconResult = {
      success: true,
      summary: a.summary, customers: a.customers, unassignedBreakdown: a.unassignedBreakdown,
      flagged: a.flagged, flaggedTotal: a.flaggedTotal, flaggedTruncated: a.flaggedTruncated,
      grandSubsidy: a.grandSubsidy, grandCredit: a.grandCredit, grandPant: a.grandPant || 0, grandTotal: a.grandTotal
    };
    this.vippsFileName = a.settlementMeta?.vipps?.fileName || '';
    this.stripeFileName = a.settlementMeta?.stripe?.fileName || '';
    this.vippsFile = null; this.stripeFile = null; this.vippsRows = null; this.stripeRows = null;
    this.reconLines = null;
    this.savedHasTransactions = !!(a.transactions && a.transactions.rows);
    this.recomputeJuice();
    this.viewingSaved = true; this.reconError = '';
    this.savedNote = `Viewing saved avstemming for ${this.invoiceMonth} (created ${a.createdBy || 'unknown'}).`;
    if (switchTab) { this.selectedTabIndex = 0; }
    // Opened from History for another month: bring the month-scoped tabs along.
    if (monthChanged) { this.loadMotemat(); this.loadDrift(); this.recomputeAvtale(); this.loadJuice(); }
  }

  exportRecon() {
    if (!this.reconResult || !this.reconResult.customers || !this.reconResult.customers.length) { return; }
    const rows: any[][] = [['Customer', 'Confirmed subsidy (ex-VAT)', 'Credit consumed (inc-VAT)', 'Billable total (ex-VAT)', 'VAT %', 'VAT', 'Total (inc-VAT)', 'Pant (ref, not billed)', 'Confirmed tx', 'Credit tx', 'Flagged']];
    this.reconResult.customers.forEach((c: any) => {
      const cust = this.customers.find(x => x.kundekortId === c.customerId);
      const rate = customerVatRate(cust);
      const amt = invoiceAmounts(c.confirmedSubsidyExVAT, c.creditConsumed, rate);
      rows.push([
        c.customerId === 'UNASSIGNED' ? 'Unassigned' : this.customerName(c.customerId),
        c.confirmedSubsidyExVAT, c.creditConsumed, amt.exVat, Math.round(rate * 100), amt.vat, amt.gross, c.pantTotal || 0,
        c.confirmedCount, c.creditCount, c.flaggedCount
      ]);
    });
    const csv = rows.map(r => r.map(x => `"${String(x).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `reconciliation-${this.invoiceMonth}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  // Full multi-sheet Excel report for cross-verification: Summary, per-customer
  // totals, every flagged/unmatched transaction WITH its order number + provider
  // (so you can look each one up in the Vipps/Stripe file), and the Unassigned
  // group breakdown.
  async exportReportXlsx() {
    if (!this.reconResult || this.reconResult.success === false) { return; }
    const XLSX = await import('xlsx');
    const wb = XLSX.utils.book_new();
    const s = this.reconResult.summary || {};
    const sheet = (aoa: any[][]) => XLSX.utils.aoa_to_sheet(aoa);

    // 1. Summary
    XLSX.utils.book_append_sheet(wb, sheet([
      ['Reconciliation report', this.invoiceMonth],
      [],
      ['Metric', 'Value'],
      ['Transactions', s.transactions],
      ['Confirmed', s.confirmed],
      ['Credit', s.credit],
      ['Unmatched (total)', s.unmatched],
      ['  · Unmatched Vipps tx', s.unmatchedVipps],
      ['  · Unmatched Stripe tx', s.unmatchedStripe],
      ['  · No order-ID', s.noOrderId],
      ['Refunded / void', s.refunded],
      ['Partial refunds', s.partialRefund],
      ['Settlement orders (total)', s.settlementOrders],
      ['  · Vipps orders in file', s.settlementVipps],
      ['  · Stripe orders in file', s.settlementStripe],
      ['Derived customer (via group)', s.derivedCustomer],
      ['  · via system (no group)', s.derivedCustomerSystem],
      ['Still unassigned', s.stillUnassigned],
      ['Zero-subsidy confirmed card', s.zeroSubsidyCard],
      [],
      ['Grand subsidy (ex-VAT)', this.reconResult.grandSubsidy],
      ['Grand credit', this.reconResult.grandCredit],
      ['Grand total', this.reconResult.grandTotal],
      ['Grand pant (reference only — not billed)', this.reconResult.grandPant],
    ]), 'Summary');

    // 2. Customers (with VAT)
    const custRows: any[][] = [['Customer', 'Confirmed subsidy (ex-VAT)', 'Credit consumed (inc-VAT)', 'Billable (ex-VAT)', 'VAT %', 'VAT', 'Total (inc-VAT)', 'Pant (ref, not billed)', 'Confirmed tx', 'Credit tx', 'Flagged']];
    (this.reconResult.customers || []).forEach((c: any) => {
      const cust = this.customers.find(x => x.kundekortId === c.customerId);
      const rate = customerVatRate(cust);
      const amt = invoiceAmounts(c.confirmedSubsidyExVAT, c.creditConsumed, rate);
      custRows.push([
        c.customerId === 'UNASSIGNED' ? 'Unassigned' : this.customerName(c.customerId),
        c.confirmedSubsidyExVAT, c.creditConsumed, amt.exVat, Math.round(rate * 100), amt.vat, amt.gross, c.pantTotal || 0,
        c.confirmedCount, c.creditCount, c.flaggedCount
      ]);
    });
    XLSX.utils.book_append_sheet(wb, sheet(custRows), 'Customers');

    // 3. Flagged & unmatched — the cross-verify sheet (order number + provider)
    const flRows: any[][] = [['Order ID', 'Provider', 'Reason', 'Customer', 'Subsidy (ex-VAT)']];
    (this.reconResult.flagged || []).forEach((f: any) => flRows.push([
      f.orderId || '', f.payType, f.reason,
      f.customerId === 'UNASSIGNED' ? 'Unassigned' : this.customerName(f.customerId),
      f.billableSubsidyExVAT
    ]));
    if (this.reconResult.flaggedTruncated) {
      flRows.push([], [`Note: showing first ${this.reconResult.flagged.length} of ${this.reconResult.flaggedTotal} flagged.`]);
    }
    XLSX.utils.book_append_sheet(wb, sheet(flRows), 'Flagged & unmatched');

    // 4. Unassigned by group / system
    if (this.reconResult.unassignedBreakdown?.length) {
      const reasonLabel: { [k: string]: string } = {
        'system-unlinked': 'Link system → customer', 'system-shared': 'Split shared system', 'group-unlinked': 'Link group → customer'
      };
      const ugRows: any[][] = [['Group / System', 'Needs', 'ID', 'Transactions', 'Billable (ex-VAT)', 'Flagged']];
      this.reconResult.unassignedBreakdown.forEach((u: any) => ugRows.push([
        u.groupName || '(no group, no system)', reasonLabel[u.reason] || '', u.groupId || u.systemId || '', u.count, u.billable, u.flaggedCount
      ]));
      XLSX.utils.book_append_sheet(wb, sheet(ugRows), 'Unassigned by group');
    }

    XLSX.writeFile(wb, `reconciliation-${this.invoiceMonth}.xlsx`);
  }

  // ---- invoice generation (from the reconciled figures) -------------------
  // Turn the confirmed per-customer reconciliation into one invoice per
  // kundekort — subsidy/credit broken down GROUP BY GROUP (so the client can
  // see how much came from which department/team) plus, when this customer
  // has a garantiomsetning shortfall for the same month, a line for that too.
  // Basis = reconResult (settlement-confirmed subsidy + credit consumed) for
  // the subsidy/credit lines, and GarantiomsetningService for the garanti
  // line — NOT the raw invoiceReport estimate. Unassigned rows and
  // zero-total customers are skipped — you can't bill a customer you can't
  // name. A customer with ONLY a garanti shortfall (no subsidy/credit
  // activity this month) still gets an invoice.
  // Stops invoice building when this tab runs an older build than the one
  // deployed: invoice lines are assembled here in the browser, so a tab opened
  // before a deploy would print the old layout from fresh data.
  private async assertLatestVersion() {
    const loaded = Array.from(document.querySelectorAll('script[src]'))
      .map(s => (s.getAttribute('src') || '').match(/main-[A-Za-z0-9]+\.js/)).find(Boolean);
    if (!loaded) { return; }  // dev server — no hashed bundle to compare
    try {
      const html = await (await fetch('/index.html', { cache: 'no-store' })).text();
      const live = html.match(/main-[A-Za-z0-9]+\.js/);
      if (live && live[0] !== loaded[0]) {
        throw new Error('A newer version of the admin panel has been deployed since this page was opened. Reload the page (Cmd+Shift+R) and try again.');
      }
    } catch (e: any) {
      if (e && /newer version/.test(e.message)) { throw e; }
      // Offline or index.html unreachable — not a reason to block billing.
    }
  }

  private async buildInvoices(): Promise<any[]> {
    await this.assertLatestVersion();
    // Consumption comes from the reconciliation on screen; coffee, juice, rent,
    // garanti and møtemat from the selected month. They must be the same month,
    // and there must BE a reconciliation — without one every subsidy and credit
    // line would silently be missing.
    if (!this.reconResult || this.reconResult.success === false) {
      throw new Error(`No reconciliation for ${this.invoiceMonth} on screen. Open the saved one (History) or upload the settlement files and run it first.`);
    }
    if (this.reconMonth && this.reconMonth !== this.invoiceMonth) {
      throw new Error(`The reconciliation on screen is for ${this.reconMonth}, but ${this.invoiceMonth} is selected. Pick the same month (or re-run) before generating invoices.`);
    }
    const from = moment(this.invoiceMonth, 'YYYY-MM').startOf('month');
    const to = moment(this.invoiceMonth, 'YYYY-MM').endOf('month');

    // A failure stops invoicing: swallowing it used to drop every garanti line
    // without a word, and the calculation now also depends on Vendon.
    const garantiByCustomer: { [id: string]: GarantiBillable } = await this.garantiSvc.getBillableByCustomer(this.invoiceMonth)
      .catch((e: any) => { throw new Error(`Could not compute garantiomsetning (${(e && e.message) || 'unknown error'}). Invoices were not built.`); });
    // Recurring contract charges (rent / driftstøtte / service) — per system, and
    // properly month-scoped, unlike driftkostnad.
    const avtaleByCustomer = await this.avtaleSvc.getBillableByCustomer(this.invoiceMonth).catch(() => ({} as { [id: string]: AvtaleBillable }));
    // Per-system "Deres referanse" override (System card) — see system-reference.service.ts.
    // Falls back to the customer's own default when a system has none of its own.
    const systemRefById = await this.systemRefSvc.getCustomerReferenceBySystem().catch(() => ({} as { [id: string]: string }));

    // Møtemat ordered through the customer portal and approved by Frigg. Same
    // company scope as the reconciliation above: every company for a super-admin
    // running the all-companies view, otherwise just the selected one.
    const motematCompanyIds = (this.showAllCompanies && this._isSuperAdmin)
      ? Object.keys(this.companiesMap)
      : [this.config.getCompanyId()];
    const motematByCustomer = await this.motematSvc
      .getBillableByCustomer(this.invoiceMonth, motematCompanyIds)
      .catch(() => ({} as { [id: string]: MotematBillable }));

    // Coffee per cup from the Vendon machines. A failure STOPS invoice building
    // rather than quietly producing invoices without coffee: an outage or a new,
    // unmapped machine is something a person has to see before anything is sent.
    const coffee: any = await this.svc.vendonCoffeeBillable(this.invoiceMonth).toPromise()
      .catch((e: any) => { throw new Error(`Could not read coffee cups from Vendon (${(e && e.error && e.error.message) || (e && e.message) || 'request failed'}). Invoices were not built.`); });
    if (!coffee || coffee.success === false) {
      throw new Error(`Could not read coffee cups from Vendon (${(coffee && coffee.message) || 'no response'}). Invoices were not built.`);
    }
    if ((coffee.errors || []).length) {
      throw new Error(`Vendon coffee data has problems — fix before invoicing: ${coffee.errors.join('; ')}`);
    }
    if ((coffee.unmapped || []).length) {
      throw new Error(`Coffee machine(s) with sales this month are not set up: ${coffee.unmapped.map((u: any) => `${u.name} (${u.vendonMachineId}, ${u.cups} cups)`).join(', ')}. Set them up on the Coffee tab (bill a customer, or switch off "Billed by Frigg") before invoicing.`);
    }
    const kaffeByCustomer: { [id: string]: { total: number, lines: any[] } } = coffee.byCustomer || {};
    // Juice entered by hand (Juice tab). Throws when an entry's system has no
    // customer — stops invoicing rather than dropping it.
    const juiceByCustomer: { [id: string]: JuiceBillable } = await this.juiceSvc.getBillableByCustomer(this.invoiceMonth);

    const reconByCustomer: { [id: string]: any } = {};
    (this.reconResult?.customers || []).forEach((c: any) => { if (c.customerId !== 'UNASSIGNED') { reconByCustomer[c.customerId] = c; } });

    // Juice goes on the invoice that carries the same system's purchases. The
    // juice service resolves a system's customer through system-kunder, while
    // purchases follow the buyer's group, and the two drift apart when billing
    // moves to another company without the system link following (Grieg Seafood,
    // August 2026: purchases and coffee to Oslo Salmon Processing, juice to
    // Grieg Seafood Sales as a separate invoice). When exactly one customer was
    // billed for a system's purchases this month, its juice follows them; a
    // system with no purchases, or purchases split across customers, keeps the
    // system link.
    const purchaseCustomer = this.juiceCustomerBySystem(await this.juiceSvc.getExplicitPayers());
    Object.keys(juiceByCustomer).forEach(owner => {
      const keep: JuiceBillable['lines'] = [];
      juiceByCustomer[owner].lines.forEach(l => {
        const target = purchaseCustomer[l.systemId] || owner;
        if (target === owner) { keep.push(l); return; }
        const moved = juiceByCustomer[target] || (juiceByCustomer[target] = { total: 0, lines: [] });
        moved.lines.push(l);
        moved.total = +(moved.total + l.amountExVat).toFixed(2);
      });
      juiceByCustomer[owner].lines = keep;
      juiceByCustomer[owner].total = +keep.reduce((n, l) => n + l.amountExVat, 0).toFixed(2);
      if (!keep.length) { delete juiceByCustomer[owner]; }
    });

    const customerIds = new Set<string>([
      ...Object.keys(reconByCustomer),
      ...Object.keys(garantiByCustomer),
      ...Object.keys(motematByCustomer),
      ...Object.keys(avtaleByCustomer),
      ...Object.keys(kaffeByCustomer),
      ...Object.keys(juiceByCustomer)
    ]);
    const invoices: any[] = [];

    // An organisation number can belong to SEVERAL kundekort — one organisation,
    // several invoice recipients (Asker Kommune's two canteens, IKEA, Akershus).
    // The ref below becomes PowerOffice's ExternalImportReference, which is its
    // duplicate guard, so two recipients sharing an org number must not build the
    // same one or the second invoice is rejected as a re-send of the first.
    // Only shared org numbers are disambiguated, so every other customer's ref
    // stays byte-for-byte what it has always been.
    const orgShareCount: { [org: string]: number } = {};
    const orgKundenrShareCount: { [orgKundenr: string]: number } = {};
    customerIds.forEach(cid => {
      const c = this.customers.find(x => x.kundekortId === cid) || {};
      if (c.orgId) {
        orgShareCount[String(c.orgId)] = (orgShareCount[String(c.orgId)] || 0) + 1;
        const k = `${c.orgId}|${c.powerOfficeCustomerNo || ''}`;
        orgKundenrShareCount[k] = (orgKundenrShareCount[k] || 0) + 1;
      }
    });

    // ---- Groups with their own reference -----------------------------------
    // A group with a "Deres referanse" of its own (group dialog) gets a separate
    // invoice for its members' purchases, even when another group of the same
    // customer carries the identical reference: Bane Nor has three groups on one
    // system, billed to BANE NOR SF as three invoices, two of them with the same
    // reference. The reconciliation has per-group and per-system totals but not
    // the two combined, so the split comes from the transaction rows: each
    // referenced group's own sums, and the rest of the customer re-aggregated per
    // system and group for the ordinary invoice below.
    const groupRefs = await this.systemRefSvc.getGroupReferences();
    const CREDIT_PAY = ['credit'];
    type GroupSplit = { groupIds: string[], groups: any[], remainder: any };
    const groupSplits: { [cid: string]: GroupSplit } = {};
    const splitCustomers = Object.keys(reconByCustomer).filter(cid => groupRefs[cid] &&
      (reconByCustomer[cid].groups || []).some((gr: any) => gr.groupId && groupRefs[cid][gr.groupId] && gr.billableTotal > 0));
    if (splitCustomers.length) {
      const { columns, rows } = await this.reconRows();
      const I: any = {};   // column name -> index in each row
      columns.forEach((k, i) => { I[k] = i; });
      const tally = () => ({ confirmedSubsidyExVAT: 0, creditConsumed: 0, pantTotal: 0, creditPantTotal: 0, billableTotal: 0, confirmedCount: 0, creditCount: 0 });
      const add = (b: any, r: any[]) => {
        const sub = +r[I.sub] || 0, cr = +r[I.cr] || 0, pant = +r[I.pant] || 0;
        b.confirmedSubsidyExVAT += sub; b.creditConsumed += cr;
        b.creditPantTotal += pant; b.pantTotal += pant;
        b.billableTotal += sub + cr + pant;
        if (sub > 0) { b.confirmedCount++; }
        if (CREDIT_PAY.includes(r[I.payType])) { b.creditCount++; }
      };
      const rounded = (b: any) => {
        ['confirmedSubsidyExVAT', 'creditConsumed', 'pantTotal', 'creditPantTotal', 'billableTotal'].forEach(k => { b[k] = +b[k].toFixed(2); });
        return b;
      };
      splitCustomers.forEach(cid => {
        const refs = groupRefs[cid];
        const whole = reconByCustomer[cid];
        const sysName: { [id: string]: string } = {};
        (whole.systems || []).forEach((sy: any) => { sysName[sy.systemId || ''] = sy.systemName; });
        const grpName: { [id: string]: string } = {};
        (whole.groups || []).forEach((gr: any) => { grpName[gr.groupId || ''] = gr.groupName; });
        // Stable per-group ref suffix (-G1, -G2 ...): PowerOffice treats the ref as
        // its duplicate guard, so it must not depend on this month's amounts.
        const order = Object.keys(refs).sort();
        const groups: { [gid: string]: any } = {};
        const rest: any = { customerId: cid, ...tally(), systems: {}, groups: {} };
        let rowTotal = 0;
        rows.forEach(r => {
          if (r[I.customerId] !== cid) { return; }
          rowTotal += (+r[I.sub] || 0) + (+r[I.cr] || 0) + (+r[I.pant] || 0);
          const gid = r[I.groupId] || '';
          if (gid && refs[gid]) {
            add(groups[gid] || (groups[gid] = { groupId: gid, name: grpName[gid] || refs[gid].name, ref: refs[gid].ref, suffix: `G${order.indexOf(gid) + 1}`, ...tally() }), r);
            return;
          }
          add(rest, r);
          const sk = r[I.systemId] || '';
          add(rest.systems[sk] || (rest.systems[sk] = { systemId: r[I.systemId] || null, systemName: sysName[sk] || null, ...tally() }), r);
          add(rest.groups[gid] || (rest.groups[gid] = { groupId: gid || null, groupName: grpName[gid] || null, ...tally() }), r);
        });
        if (Math.abs(rowTotal - whole.billableTotal) > 1) {
          throw new Error(`The transaction rows for ${this.customerName(cid)} (${rowTotal.toFixed(2)}) do not match the reconciliation (${whole.billableTotal}). Re-run the reconciliation for ${this.invoiceMonth}.`);
        }
        groupSplits[cid] = {
          groupIds: order,
          groups: Object.values(groups).map(rounded).filter((gr: any) => gr.billableTotal > 0)
            .sort((a: any, b: any) => a.suffix.localeCompare(b.suffix, undefined, { numeric: true })),
          remainder: { ...rounded(rest), systems: Object.values(rest.systems).map(rounded), groups: Object.values(rest.groups).map(rounded) }
        };
      });
    }

    customerIds.forEach(cid => {
      const groupSplit = groupSplits[cid];
      // With referenced groups split off, the ordinary invoice carries the rest.
      const c = groupSplit ? groupSplit.remainder : reconByCustomer[cid];
      const g = garantiByCustomer[cid];
      const m = motematByCustomer[cid];
      const av = avtaleByCustomer[cid];
      const kf = kaffeByCustomer[cid];
      const ju = juiceByCustomer[cid];
      const hasRecon = c && c.billableTotal > 0;
      const hasGaranti = g && (g.billable > 0 || g.kickback > 0);
      const hasMotemat = m && m.grossIncVat > 0;
      // The admin fee is on for every real kundekort unless switched off on the
      // card — it no longer depends on the customer "owning" a system, which
      // made one of two cards sharing a system go without it (Asker, EDA).
      const feeCust = this.customers.find(x => x.kundekortId === cid);
      const hasDrift = !!feeCust && feeCust.driftkostnaderEnabled !== false;
      const hasAvtale = av && av.lines.length > 0;
      const hasKaffe = kf && kf.lines.length > 0;
      const hasJuice = ju && ju.lines.length > 0;
      // hasDrift is deliberately NOT here: the admin fee rides on an invoice, it
      // does not justify one (see pushInvoice).
      if (!hasRecon && !hasGaranti && !hasMotemat && !hasAvtale && !hasKaffe && !hasJuice && !groupSplit) { return; }

      const cust = this.customers.find(x => x.kundekortId === cid) || {};
      const vatRate = customerVatRate(cust);
      const defaultRef = (cust.customerReference || '').trim();
      // A system's own override (System card) wins; otherwise the customer's
      // default. Most customers never set a system override, so this resolves
      // to defaultRef everywhere and nothing below actually splits.
      const refFor = (systemId: string | null | undefined) =>
        (systemId && systemRefById[systemId]) || defaultRef;

      // Which references are actually in play for this customer's billable
      // systems this month? size <= 1 → one invoice, exactly like before
      // this feature existed. size > 1 → genuinely split billing.
      const refSet = new Set<string>();
      if (hasRecon) { (c.systems || []).filter((s: any) => s.billableTotal > 0).forEach((s: any) => refSet.add(refFor(s.systemId))); }
      if (hasGaranti) { g.systems.forEach(s => refSet.add(refFor(s.systemId))); }
      if (hasAvtale) { av.lines.forEach(l => refSet.add(refFor(l.systemId))); }
      if (hasKaffe) { kf.lines.forEach(l => refSet.add(refFor(l.systemId))); }
      if (hasJuice) { ju.lines.forEach(l => refSet.add(refFor(l.systemId))); }
      // Møtemat is NOT here: it gets an invoice of its own further down, so it
      // must not influence how the consumption invoice splits.
      const splitting = refSet.size > 1;
      // Everything that belongs on the consumption invoice(s). A customer with
      // nothing but møtemat gets no consumption invoice at all, rather than an
      // empty one.
      const hasMain = hasRecon || hasGaranti || hasAvtale || hasKaffe || hasJuice;

      const recipient = {
        name: cust.legalName || cust.name || '(unknown customer)',
        orgId: cust.orgId || '',
        address: cust.address || '',
        address2: cust.address2 || '',
        zipCity: [cust.zipCode, cust.city].filter(Boolean).join(' '),
        country: cust.country || '',
        email: cust.email || ''
      };
      const orgSuffix = (cust.orgId || cid || '').toString().slice(-6);
      // Kundenr when this organisation has more than one recipient in this run —
      // it is what distinguishes them in PowerOffice anyway. Cards sharing an
      // organisation AND a Kundenr fall back to the document id, which is unique
      // by construction.
      // Asker Kommune Velferd and Fusdal share both (920125298, 30319): the
      // Kundenr alone gave them one ref and blocked both in the PowerOffice check.
      const shared = cust.orgId && orgShareCount[String(cust.orgId)] > 1;
      const sharedKundenr = shared && cust.powerOfficeCustomerNo
        && orgKundenrShareCount[`${cust.orgId}|${cust.powerOfficeCustomerNo}`] > 1;
      const discriminator = !shared ? ''
        : sharedKundenr ? `-${cust.powerOfficeCustomerNo}-${cid.toString().slice(-6)}`
        : `-${cust.powerOfficeCustomerNo || cid.toString().slice(-6)}`;
      const baseRef = `${this.invoiceMonth.replace('-', '')}-${orgSuffix}${discriminator}`;
      let sliceIndex = 0;
      const pushInvoice = (customerReference: string, lines: any[], subtotalExVatRaw: number, vatExemptTotalRaw: number,
                           opts: { refSuffix?: string, noDrift?: boolean, specSystemIds?: (string | null)[] | null, specGroupIds?: string[] } = {}) => {
        // Administrasjonsgebyr: ONE per invoice, not one per system. Confirmed
        // against issued invoice 13591, which carries a single 59,00 line at 25%
        // regardless of how many systems the customer runs. A customer can opt out
        // via the Driftskostnader toggle on their kundekort.
        //
        // It rides along on an invoice that exists for some other reason — it never
        // creates one on its own, so a customer with no consumption and no agreement
        // is not invoiced purely to be charged a fee.
        if (hasDrift && !opts.noDrift && lines.length) {
          lines = [...lines, {
            kind: 'drift',
            description: `Administrasjonsgebyr — ${from.format('MMMM YYYY')}`,
            qty: 1,
            amountExVat: DRIFTKOSTNAD_RATE_EX_VAT
          }];
          subtotalExVatRaw += DRIFTKOSTNAD_RATE_EX_VAT;
        }
        const subtotalExVat = +subtotalExVatRaw.toFixed(2);
        const vatExemptTotal = +vatExemptTotalRaw.toFixed(2);

        // VAT per line kind. The standard-rated lines are summed explicitly and the
        // REMAINDER of the subtotal is charged at the customer's own rate — rather
        // than summing the customer-rated lines directly — so any øre of rounding
        // between the line amounts and subtotalExVat stays in the customer bucket.
        // With no standard-rated lines present this is arithmetically identical to
        // the single-rate calculation it replaces, so existing invoices don't move.
        const standardExVat = +lines
          .filter((l: any) => VAT_BASIS_BY_KIND[l.kind] === 'standard')
          .reduce((n: number, l: any) => n + (Number(l.amountExVat) || 0), 0).toFixed(2);
        const customerExVat = +(subtotalExVat - standardExVat).toFixed(2);
        const vatAmount = +(customerExVat * vatRate + standardExVat * STANDARD_VAT_RATE).toFixed(2);
        invoices.push({
          customerId: cid,
          recipient,
          // Unique per slice only when actually splitting, so a non-splitting
          // customer's ref is byte-for-byte what it always was — no surprise
          // "new invoice" on the PowerOffice side for anyone not using this.
          ref: opts.refSuffix ? `${baseRef}-${opts.refSuffix}`
                             : (splitting ? `${baseRef}-${++sliceIndex}` : baseRef),
          ourReference: (cust.ourReference || '').trim() || SELLER.ourReference,
          customerReference,
          // Whose transactions make up this invoice's subsidy/credit/pant lines,
          // for the attached specification: null = all of this customer's, a
          // list = only purchases made at those systems (split billing).
          specSystemIds: opts.specSystemIds === undefined ? [] : opts.specSystemIds,
          // ...and which groups: a list = only these groups' purchases (a group
          // with its own reference); otherwise every group except those.
          specGroupIds: opts.specGroupIds || null,
          specExcludeGroupIds: opts.specGroupIds ? null : (groupSplit ? groupSplit.groupIds : null),
          periodFrom: from.format('YYYY-MM-DD'),
          periodTo: to.format('YYYY-MM-DD'),
          lines,
          subtotalExVat,
          vatRate,
          vatAmount,
          // The per-rate split, so the printed invoice can show one VAT row per rate
          // instead of a single misleading "Mva 15%" over a mixed-rate invoice.
          vatBreakdown: [
            { rate: vatRate, base: customerExVat, vat: +(customerExVat * vatRate).toFixed(2) },
            { rate: STANDARD_VAT_RATE, base: standardExVat, vat: +(standardExVat * STANDARD_VAT_RATE).toFixed(2) }
          ].filter(b => b.base !== 0),
          vatExemptTotal,
          totalIncVat: +(subtotalExVat + vatAmount + vatExemptTotal).toFixed(2)
        });
      };

      // ---- Møtemat — always its own invoice ------------------------------
      // Meeting food is bought by the customer for a named occasion, not
      // consumed by employees against a subsidy, and they reconcile it against
      // that occasion's own reference. Mixing it into the consumption invoice
      // made it impossible to pass on: it now gets one invoice of its own, with
      // a `-MM` ref so PowerOffice sees it as a distinct document rather than a
      // duplicate of the month's consumption invoice.
      //
      // No administrasjonsgebyr rides along — the fee is for operating the
      // systems, not for catering, and it is already charged once on the
      // consumption invoice.
      const pushMotematInvoice = () => {
        if (!hasMotemat) { return; }
        const lines: any[] = [];
        let subtotalExVat = 0;
        // The portal's "Referanse/PO" field is stored as `desciption`.
        const orderRefs = Array.from(new Set(m.orders.map(o => (o.desciption || '').trim()).filter(Boolean)));
        // One shared reference for the month goes in the invoice header; several
        // different ones can't, so each stays on its own line and the header
        // falls back to the customer's default.
        const motematRef = orderRefs.length === 1 ? orderRefs[0] : defaultRef;
        m.orders.forEach(o => {
          // The stored total is VAT-INCLUSIVE (the price the portal quoted), so the
          // customer's rate is BACKED OUT rather than added — the same treatment
          // creditConsumed gets, and the reason the invoiced gross equals the quote.
          const exVat = +(vatRate > 0 ? o.grossIncVat / (1 + vatRate) : o.grossIncVat).toFixed(2);
          const what = o.desciption ? ` (${o.desciption})` : '';
          lines.push({
            kind: 'motemat',
            description: `Møtemat #${o.deliveryNumber} — ${o.dateLabel}${what}${o.systemName ? ' — ' + o.systemName : ''}`,
            qty: o.itemCount,
            amountExVat: exVat
          });
          // Accumulate the ROUNDED line, not the raw quotient, so the printed
          // lines add up to the subtotal exactly.
          subtotalExVat += exVat;
        });
        pushInvoice(motematRef, lines, subtotalExVat, 0, { refSuffix: 'MM', noDrift: true, specSystemIds: [] });
      };

      // One invoice per group with its own reference (see groupSplits above).
      // Each carries the administrasjonsgebyr like any other invoice.
      if (groupSplit) {
        groupSplit.groups.forEach((gr: any) => {
          const amt = invoiceAmounts(gr.confirmedSubsidyExVAT, gr.creditConsumed, vatRate);
          const lines: any[] = [];
          if (gr.confirmedSubsidyExVAT > 0) {
            lines.push({ kind: 'subsidy', description: `Subsidiert kjøp — ${gr.name} — ${from.format('MMMM YYYY')}`, qty: gr.confirmedCount, amountExVat: +amt.subExVat.toFixed(2) });
          }
          if (gr.creditConsumed > 0) {
            lines.push({ kind: 'credit', description: `Kredittforbruk — ${gr.name} — ${from.format('MMMM YYYY')}`, qty: gr.creditCount, amountExVat: +amt.creditExVat.toFixed(2) });
          }
          if (gr.creditPantTotal > 0) {
            lines.push({ kind: 'pant', description: `Pant (betalt med kreditt, mva-fritt) — ${gr.name} — ${from.format('MMMM YYYY')}`, qty: '', amountExVat: gr.creditPantTotal });
          }
          pushInvoice(gr.ref, lines, amt.exVat, gr.creditPantTotal, { refSuffix: gr.suffix, specSystemIds: null, specGroupIds: [gr.groupId] });
        });
      }

      // One line per coffee machine: "Kaffe — Sparebanken Øst Hokksund — 131 × 10,50".
      const kaffeLine = (l: any) => ({
        kind: 'kaffe',
        description: `Kaffe — ${l.label} — ${l.cups} × ${Number(l.pricePerCupExVat).toFixed(2).replace('.', ',')} — ${from.format('MMMM YYYY')}`,
        qty: l.cups,
        amountExVat: +Number(l.amountExVat).toFixed(2)
      });

      const juiceLine = (l: any) => ({
        kind: 'juice',
        description: `Juice — ${l.systemName} — ${l.cups} × ${Number(l.pricePerCupExVat).toFixed(2).replace('.', ',')} — ${from.format('MMMM YYYY')}`,
        qty: l.cups,
        amountExVat: +Number(l.amountExVat).toFixed(2)
      });

      // Garanti shortfall and kickback, per system (or pooled period): either can
      // be there without the other — sales above the guarantee still earn the
      // full kickback, as a minus line.
      const garantiLines = (s: GarantiBillable['systems'][number]) => {
        const period = s.period || from.format('MMMM YYYY');
        const out: any[] = [];
        if (s.billable > 0) {
          out.push({ kind: 'garanti', description: `Garantiomsetning — differanse (${s.name}) — ${period}`, qty: '', amountExVat: s.billable });
        }
        if (s.kickback > 0) {
          out.push({ kind: 'kickback', description: `Kickback garantiomsetning (${s.name}) — ${period}`, qty: '', amountExVat: -s.kickback });
        }
        return out;
      };

      if (hasMain && !splitting) {
        // ---- Single invoice — identical to how this always worked ----------
        const lines: any[] = [];
        let subtotalExVat = 0;
        // Pant is legally VAT-exempt in Norway (it's a deposit, not a taxable
        // sale) — kept OUT of subtotalExVat (which the customer's VAT rate
        // gets applied to below) and added to the invoice total untaxed.
        let vatExemptTotal = 0;

        if (hasRecon) {
          // Per-SITE lines ("Hokksund", "Bragernes") — the layout the hand-made
          // PowerOffice invoices use and customers reconcile against. Group names
          // are internal ("Group1 100%") and mean nothing on the customer's side.
          // Falls back to per-group, then one aggregate line, for older saved
          // avstemminger that predate the per-system breakdown.
          const sites = (c.systems || []).filter((s: any) => s.confirmedSubsidyExVAT > 0 || s.creditConsumed > 0 || s.creditPantTotal > 0);
          const groups = (c.groups || []).filter((gr: any) => gr.confirmedSubsidyExVAT > 0 || gr.creditConsumed > 0 || gr.creditPantTotal > 0);
          let sitePant = 0;
          if (cust.invoiceLinesByGroup === true && groups.length) {
            // "Show groups on invoice" (kundekort): one set of lines per group, for
            // customers who pass the cost on per department. Two groups with the
            // same name get their group id added, or the lines can't be told apart.
            const nameCount: { [n: string]: number } = {};
            groups.forEach((gr: any) => { const n = gr.groupName || ''; nameCount[n] = (nameCount[n] || 0) + 1; });
            groups.forEach((gr: any) => {
              const base = gr.groupName || (gr.groupId ? gr.groupId : 'Uten gruppe');
              const label = gr.groupId && nameCount[gr.groupName || ''] > 1 ? `${base} (${gr.groupId})` : base;
              const amt = invoiceAmounts(gr.confirmedSubsidyExVAT, gr.creditConsumed, vatRate);
              if (gr.confirmedSubsidyExVAT > 0) {
                lines.push({ kind: 'subsidy', description: `Subsidiert kjøp — ${label} — ${from.format('MMMM YYYY')}`, qty: gr.confirmedCount, amountExVat: +amt.subExVat.toFixed(2) });
              }
              if (gr.creditConsumed > 0) {
                lines.push({ kind: 'credit', description: `Kredittforbruk — ${label} — ${from.format('MMMM YYYY')}`, qty: gr.creditCount, amountExVat: +amt.creditExVat.toFixed(2) });
              }
              const pant = +(gr.creditPantTotal || 0);
              if (pant > 0) {
                lines.push({ kind: 'pant', description: `Pant (betalt med kreditt, mva-fritt) — ${label} — ${from.format('MMMM YYYY')}`, qty: '', amountExVat: +pant.toFixed(2) });
                sitePant += pant;
              }
            });
          } else if (sites.length) {
            sites.forEach((s: any) => {
              const label = s.systemName || (s.systemId ? s.systemId : 'Ukjent system');
              const amt = invoiceAmounts(s.confirmedSubsidyExVAT, s.creditConsumed, vatRate);
              if (s.confirmedSubsidyExVAT > 0) {
                lines.push({ kind: 'subsidy', description: `Subsidiert kjøp — ${label} — ${from.format('MMMM YYYY')}`, qty: s.confirmedCount, amountExVat: +amt.subExVat.toFixed(2) });
              }
              if (s.creditConsumed > 0) {
                lines.push({ kind: 'credit', description: `Kredittforbruk — ${label} — ${from.format('MMMM YYYY')}`, qty: s.creditCount, amountExVat: +amt.creditExVat.toFixed(2) });
              }
              const pant = +(s.creditPantTotal || 0);
              if (pant > 0) {
                lines.push({ kind: 'pant', description: `Pant (betalt med kreditt, mva-fritt) — ${label} — ${from.format('MMMM YYYY')}`, qty: '', amountExVat: +pant.toFixed(2) });
                sitePant += pant;
              }
            });
          } else if (groups.length) {
            groups.forEach((gr: any) => {
              const label = gr.groupName || (gr.groupId ? gr.groupId : 'Uten gruppe');
              const amt = invoiceAmounts(gr.confirmedSubsidyExVAT, gr.creditConsumed, vatRate);
              if (gr.confirmedSubsidyExVAT > 0) {
                lines.push({ kind: 'subsidy', description: `Subsidiert kjøp — ${label} — ${from.format('MMMM YYYY')}`, qty: gr.confirmedCount, amountExVat: +amt.subExVat.toFixed(2) });
              }
              if (gr.creditConsumed > 0) {
                lines.push({ kind: 'credit', description: `Kredittforbruk — ${label} — ${from.format('MMMM YYYY')}`, qty: gr.creditCount, amountExVat: +amt.creditExVat.toFixed(2) });
              }
            });
          } else {
            const amt = invoiceAmounts(c.confirmedSubsidyExVAT, c.creditConsumed, vatRate);
            if (c.confirmedSubsidyExVAT > 0) {
              lines.push({ kind: 'subsidy', description: `Subsidiert kjøp (bekreftet oppgjør) — ${from.format('MMMM YYYY')}`, qty: c.confirmedCount, amountExVat: +amt.subExVat.toFixed(2) });
            }
            if (c.creditConsumed > 0) {
              lines.push({ kind: 'credit', description: `Kredittforbruk — ${from.format('MMMM YYYY')}`, qty: c.creditCount, amountExVat: +amt.creditExVat.toFixed(2) });
            }
          }
          const amtTotal = invoiceAmounts(c.confirmedSubsidyExVAT, c.creditConsumed, vatRate);
          subtotalExVat += amtTotal.exVat;

          // Pant paid with credit — VAT-exempt, so it goes into vatExemptTotal,
          // NOT subtotalExVat. Per-site lines were emitted above; whatever the
          // customer total holds beyond them (pant not attributed to a system)
          // gets one aggregate line so it is never dropped. Older saves lack the
          // customer-level total, hence the max.
          vatExemptTotal += sitePant;
          const restPant = +(Math.max(+(c.creditPantTotal || 0), sitePant) - sitePant).toFixed(2);
          if (restPant > 0) {
            lines.push({ kind: 'pant', description: `Pant (betalt med kreditt, mva-fritt) — ${from.format('MMMM YYYY')}`, qty: '', amountExVat: restPant });
            vatExemptTotal += restPant;
          }
        }

        if (hasGaranti) {
          // Ex-VAT shortfall figure — grossed up with the same customer VAT rate
          // as the subsidy/credit lines above, for one consistent invoice total.
          // The kickback follows as its own minus line (25%, see VAT_BASIS_BY_KIND).
          g.systems.forEach(s => lines.push(...garantiLines(s)));
          subtotalExVat += g.billable - g.kickback;
        }

        if (hasAvtale) {
          // One line per agreement, wording taken from the agreement itself so the
          // invoice reads the same as the PowerOffice originals these replace
          // ("Leie friggkantine per måned", "Driftstøtte Friggkantine per måned").
          av.lines.forEach(l => {
            lines.push({
              kind: l.kind,
              description: `${l.description || l.kind} — ${l.systemName} — ${from.format('MMMM YYYY')}`,
              qty: 1,
              amountExVat: l.amountExVat
            });
            subtotalExVat += l.amountExVat;
          });
        }

        if (hasKaffe) {
          kf.lines.forEach(l => { const line = kaffeLine(l); lines.push(line); subtotalExVat += line.amountExVat; });
        }
        if (hasJuice) {
          ju.lines.forEach(l => { const line = juiceLine(l); lines.push(line); subtotalExVat += line.amountExVat; });
        }

        pushInvoice(defaultRef, lines, subtotalExVat, vatExemptTotal, { specSystemIds: hasRecon ? null : [] });
        // This branch returns, so møtemat has to be emitted here too — it is a
        // separate invoice, not part of the one just pushed.
        pushMotematInvoice();
        return;
      }

      // ---- Split billing — this customer's systems disagree on reference ---
      // One invoice per distinct reference. Subsidy/credit/pant are sliced by
      // c.systems (not c.groups) here, since the split key is the SYSTEM the
      // purchase happened at, not the group — a system can serve more than one
      // group, and a group's purchases can span systems with different refs.
      // specSystemIds: the systems whose purchases are on this slice; null once
      // the whole customer's consumption lands here (older saves, see below).
      type Slice = { lines: any[], subtotalExVat: number, vatExemptTotal: number, specSystemIds: (string | null)[] | null };
      const byRef: { [ref: string]: Slice } = {};
      const sliceFor = (ref: string): Slice => byRef[ref] || (byRef[ref] = { lines: [], subtotalExVat: 0, vatExemptTotal: 0, specSystemIds: [] });

      if (hasRecon) {
        const reconSystems = (c.systems || []).filter((s: any) => s.billableTotal > 0);
        if (reconSystems.length) {
          reconSystems.forEach((s: any) => {
            const slice = sliceFor(refFor(s.systemId));
            if (slice.specSystemIds) { slice.specSystemIds.push(s.systemId || null); }
            const label = s.systemName || (s.systemId ? s.systemId : 'Ukjent system');
            const amt = invoiceAmounts(s.confirmedSubsidyExVAT, s.creditConsumed, vatRate);
            if (s.confirmedSubsidyExVAT > 0) {
              slice.lines.push({ kind: 'subsidy', description: `Subsidiert kjøp — ${label} — ${from.format('MMMM YYYY')}`, qty: s.confirmedCount, amountExVat: +amt.subExVat.toFixed(2) });
            }
            if (s.creditConsumed > 0) {
              slice.lines.push({ kind: 'credit', description: `Kredittforbruk — ${label} — ${from.format('MMMM YYYY')}`, qty: s.creditCount, amountExVat: +amt.creditExVat.toFixed(2) });
            }
            slice.subtotalExVat += amt.exVat;
            const creditPant = +(s.creditPantTotal || 0);
            if (creditPant > 0) {
              slice.lines.push({ kind: 'pant', description: `Pant (betalt med kreditt, mva-fritt) — ${label} — ${from.format('MMMM YYYY')}`, qty: '', amountExVat: +creditPant.toFixed(2) });
              slice.vatExemptTotal += creditPant;
            }
          });
        } else {
          // No per-system breakdown to slice by — but there IS money to bill.
          // Saved avstemminger created before `systems` was added to the reconcile
          // result carry `groups` only, and a customer whose systems disagree on
          // reference takes this split branch. Slicing an absent array silently
          // dropped EVERY subsidy and credit line, leaving an invoice that showed
          // just the driftskostnad line (which builds its own system list) — a
          // 118 kr invoice where 7 220 kr was due.
          //
          // The amounts are known even when their per-system split is not, so they
          // all go on the customer's DEFAULT reference slice. Same fallback the
          // single-invoice branch above already applies to these older saves; it
          // can put a line on a different invoice than a fresh reconcile would,
          // which is strictly better than omitting it. Re-run the reconcile for
          // the month to get the true per-system split.
          const slice = sliceFor(defaultRef);
          slice.specSystemIds = null;
          const groups = (c.groups || []).filter((gr: any) => gr.confirmedSubsidyExVAT > 0 || gr.creditConsumed > 0);
          if (groups.length) {
            groups.forEach((gr: any) => {
              const label = gr.groupName || (gr.groupId ? gr.groupId : 'Uten gruppe');
              const amt = invoiceAmounts(gr.confirmedSubsidyExVAT, gr.creditConsumed, vatRate);
              if (gr.confirmedSubsidyExVAT > 0) {
                slice.lines.push({ kind: 'subsidy', description: `Subsidiert kjøp — ${label} — ${from.format('MMMM YYYY')}`, qty: gr.confirmedCount, amountExVat: +amt.subExVat.toFixed(2) });
              }
              if (gr.creditConsumed > 0) {
                slice.lines.push({ kind: 'credit', description: `Kredittforbruk — ${label} — ${from.format('MMMM YYYY')}`, qty: gr.creditCount, amountExVat: +amt.creditExVat.toFixed(2) });
              }
            });
          } else {
            const amt = invoiceAmounts(c.confirmedSubsidyExVAT, c.creditConsumed, vatRate);
            if (c.confirmedSubsidyExVAT > 0) {
              slice.lines.push({ kind: 'subsidy', description: `Subsidiert kjøp (bekreftet oppgjør) — ${from.format('MMMM YYYY')}`, qty: c.confirmedCount, amountExVat: +amt.subExVat.toFixed(2) });
            }
            if (c.creditConsumed > 0) {
              slice.lines.push({ kind: 'credit', description: `Kredittforbruk — ${from.format('MMMM YYYY')}`, qty: c.creditCount, amountExVat: +amt.creditExVat.toFixed(2) });
            }
          }
          const amtTotal = invoiceAmounts(c.confirmedSubsidyExVAT, c.creditConsumed, vatRate);
          slice.subtotalExVat += amtTotal.exVat;
          const creditPant = +(c.creditPantTotal || 0);
          if (creditPant > 0) {
            slice.lines.push({ kind: 'pant', description: `Pant (betalt med kreditt, mva-fritt) — ${from.format('MMMM YYYY')}`, qty: '', amountExVat: +creditPant.toFixed(2) });
            slice.vatExemptTotal += creditPant;
          }
        }
      }

      if (hasGaranti) {
        g.systems.forEach(s => {
          const slice = sliceFor(refFor(s.systemId));
          slice.lines.push(...garantiLines(s));
          slice.subtotalExVat += s.billable - s.kickback;
        });
      }

      if (hasAvtale) {
        // Each agreement follows its own system's reference — which is the whole
        // point of the split: Asker's two sites carry different rents and land on
        // two different invoices (PowerOffice 13591 vs 13618).
        av.lines.forEach(l => {
          const slice = sliceFor(refFor(l.systemId));
          slice.lines.push({
            kind: l.kind,
            description: `${l.description || l.kind} — ${l.systemName} — ${from.format('MMMM YYYY')}`,
            qty: 1,
            amountExVat: l.amountExVat
          });
          slice.subtotalExVat += l.amountExVat;
        });
      }

      if (hasKaffe) {
        // Each machine follows its system's reference, like the contract lines.
        kf.lines.forEach(l => {
          const slice = sliceFor(refFor(l.systemId));
          const line = kaffeLine(l);
          slice.lines.push(line);
          slice.subtotalExVat += line.amountExVat;
        });
      }

      if (hasJuice) {
        ju.lines.forEach(l => {
          const slice = sliceFor(refFor(l.systemId));
          const line = juiceLine(l);
          slice.lines.push(line);
          slice.subtotalExVat += line.amountExVat;
        });
      }

      Object.keys(byRef).forEach(ref => {
        const slice = byRef[ref];
        pushInvoice(ref, slice.lines, slice.subtotalExVat, slice.vatExemptTotal, { specSystemIds: slice.specSystemIds });
      });

      pushMotematInvoice();
    });

    // Only Active kundekort are invoiced. Lead / Prospect / Inactive cards are
    // left out of print, Excel, specs and PowerOffice alike, and named in a
    // warning with what they would have been billed, so nothing drops silently.
    const notActive: { [cid: string]: { name: string, category: string, total: number } } = {};
    const active = invoices.filter(inv => {
      const card = this.customers.find(x => x.kundekortId === inv.customerId);
      if (!card || card.category === 'Active') { return true; }
      const n = notActive[inv.customerId] || (notActive[inv.customerId] = { name: this.customerName(inv.customerId), category: card.category || 'no category', total: 0 });
      n.total += inv.subtotalExVat;
      return false;
    });
    const skipped = Object.values(notActive);
    this.notActiveNote = skipped.length
      ? `Not invoiced, kundekort is not Active: ${skipped.map(n => `${n.name} (${n.category}, ${n.total.toFixed(2)} ex VAT)`).join(', ')}. Set the card to Active to invoice it.`
      : '';
    return active;
  }

  // Render every invoice into a print window (one A4 page each) so the operator
  // can save them as PDF / print. PowerOffice Go will later consume the same
  // buildInvoices() objects instead of this print path.
  async printInvoices() {
    if (!this.invoiceMonth) { return; }
    // Open the window SYNCHRONOUSLY, before any await — otherwise the async
    // Firestore reads in buildInvoices() break the "user gesture" chain and
    // most browsers silently block the popup.
    const w = window.open('', '_blank');
    if (!w) { this.reconError = 'Could not open the print window (popup blocked?).'; return; }
    this.generatingInvoices = true; this.reconError = '';
    try {
      const invoices = await this.buildInvoices();
      if (!invoices.length) { w.close(); this.reconError = 'No billable customers to invoice for this month (no confirmed subsidy/credit and no garantiomsetning shortfall).'; return; }
      w.document.write(this.invoicesHtml(invoices));
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 300);
    } catch (err) {
      w.close();
      this.reconError = (err && (err as any).message) || 'Could not generate invoices.';
    } finally {
      this.generatingInvoices = false;
    }
  }

  // The same buildInvoices() output as printing and PowerOffice, as a workbook:
  // one row per invoice, one row per line, and totals per line type — for the
  // accountant to check against their own basis before anything is sent.
  async exportInvoicesExcel() {
    if (!this.invoiceMonth) { return; }
    this.exportingInvoices = true; this.reconError = '';
    try {
      const invoices = await this.buildInvoices();
      if (!invoices.length) { this.reconError = 'No billable customers to invoice for this month.'; return; }
      const XLSX = await import('xlsx');
      const KIND_LABEL: { [k: string]: string } = {
        subsidy: 'Subsidie', credit: 'Kreditt', pant: 'Pant', garanti: 'Garantiomsetning', kickback: 'Kickback', motemat: 'Møtemat',
        kaffe: 'Kaffe', juice: 'Juice', leie: 'Leie', driftstotte: 'Driftstøtte', service: 'Service',
        annet: 'Annet', drift: 'Administrasjonsgebyr'
      };
      const r2 = (n: number) => +(+n || 0).toFixed(2);
      const lineVatRate = (inv: any, kind: string) => {
        const basis = VAT_BASIS_BY_KIND[kind] || 'customer';
        return basis === 'standard' ? STANDARD_VAT_RATE : (basis === 'exempt' ? 0 : inv.vatRate);
      };
      const kundenr = (cid: string) => (this.customers.find(x => x.kundekortId === cid) || {}).powerOfficeCustomerNo || '';

      const invRows: any[][] = [['Customer', 'Kundenr', 'Org no', 'Invoice ref', 'Deres ref', 'Vår ref', 'Period', 'Lines',
        'Net ex VAT', 'VAT', 'Pant (VAT-free)', 'Total incl VAT', 'Email']];
      const lineRows: any[][] = [['Invoice ref', 'Customer', 'Kundenr', 'Type', 'Description', 'Qty',
        'Amount ex VAT', 'VAT %', 'VAT', 'Amount incl VAT']];
      const byKind: { [k: string]: { n: number, ex: number, vat: number } } = {};
      const tot = { net: 0, vat: 0, pant: 0, total: 0 };
      invoices.forEach(inv => {
        const pant = r2(inv.vatExemptTotal);
        invRows.push([inv.recipient.name, kundenr(inv.customerId), inv.recipient.orgId, inv.ref, inv.customerReference || '',
          inv.ourReference || '', `${inv.periodFrom} – ${inv.periodTo}`, inv.lines.length,
          r2(inv.subtotalExVat), r2(inv.vatAmount), pant, r2(inv.totalIncVat), inv.recipient.email || '']);
        tot.net += inv.subtotalExVat; tot.vat += inv.vatAmount; tot.pant += pant; tot.total += inv.totalIncVat;
        inv.lines.forEach((l: any) => {
          const rate = lineVatRate(inv, l.kind);
          const ex = r2(l.amountExVat); const vat = r2(ex * rate);
          lineRows.push([inv.ref, inv.recipient.name, kundenr(inv.customerId), KIND_LABEL[l.kind] || l.kind, l.description,
            l.qty === '' ? '' : l.qty, ex, Math.round(rate * 100), vat, r2(ex + vat)]);
          const k = byKind[l.kind] || (byKind[l.kind] = { n: 0, ex: 0, vat: 0 });
          k.n++; k.ex += ex; k.vat += vat;
        });
      });
      invRows.push([]);
      invRows.push(['TOTAL', '', '', `${invoices.length} invoices`, '', '', '', '', r2(tot.net), r2(tot.vat), r2(tot.pant), r2(tot.total), '']);
      const kindRows: any[][] = [['Type', 'Lines', 'Amount ex VAT', 'VAT', 'Amount incl VAT']];
      Object.entries(byKind).sort((a, b) => b[1].ex - a[1].ex)
        .forEach(([k, v]) => kindRows.push([KIND_LABEL[k] || k, v.n, r2(v.ex), r2(v.vat), r2(v.ex + v.vat)]));
      kindRows.push([]);
      kindRows.push(['Note', 'Line VAT is per line; the invoice VAT on the Invoices sheet is authoritative and can differ by øre (rounding).']);

      const wb = XLSX.utils.book_new();
      const add = (rows: any[][], name: string, widths: number[]) => {
        const ws = XLSX.utils.aoa_to_sheet(rows);
        ws['!cols'] = widths.map(w => ({ wch: w }));
        XLSX.utils.book_append_sheet(wb, ws, name);
      };
      add(invRows, 'Invoices', [36, 9, 12, 22, 24, 22, 24, 6, 13, 12, 12, 14, 30]);
      add(lineRows, 'Lines', [22, 36, 9, 18, 70, 7, 13, 6, 11, 14]);
      add(kindRows, 'By type', [22, 7, 14, 12, 15]);
      XLSX.writeFile(wb, `faktura-${this.invoiceMonth}.xlsx`);
    } catch (err) {
      this.reconError = (err && (err as any).message) || 'Could not export invoices.';
    } finally {
      this.exportingInvoices = false;
    }
  }

  // ---- transaction specification (one workbook per invoice) ---------------
  // The rows behind the reconciliation on screen: in memory after a fresh run,
  // read from the saved avstemming otherwise.
  private async reconRows(): Promise<{ columns: string[], rows: any[][] }> {
    if (this.reconLines && this.reconLines.rows.length) { return this.reconLines; }
    if (this.viewingSaved) {
      if (!this.savedHasTransactions) {
        throw new Error(`The saved avstemming for ${this.invoiceMonth} was made before transaction specifications existed. Delete it (History), upload the settlement files and run it again.`);
      }
      this.reconLines = await this.avstemming.loadTransactions(this.invoiceMonth);
      return this.reconLines;
    }
    throw new Error('This reconciliation returned no transaction rows. Run it again.');
  }

  // Everything needed to build specifications for a batch of invoices: the rows
  // grouped per customer, and system/group names from the reconciliation.
  private async specContext() {
    const { columns, rows } = await this.reconRows();
    const I: any = {};   // column name -> index in each row
    columns.forEach((k, i) => { I[k] = i; });
    const byCustomer: { [cid: string]: any[][] } = {};
    rows.forEach(r => { (byCustomer[r[I.customerId]] || (byCustomer[r[I.customerId]] = [])).push(r); });
    const systemName: { [id: string]: string } = {};
    const groupName: { [id: string]: string } = {};
    (this.reconResult?.customers || []).forEach((c: any) => {
      (c.systems || []).forEach((s: any) => { if (s.systemId) { systemName[s.systemId] = s.systemName || s.systemId; } });
      (c.groups || []).forEach((g: any) => { if (g.groupId) { groupName[g.groupId] = g.groupName || g.groupId; } });
    });
    return { I, byCustomer, systemName, groupName };
  }

  // The specification for one invoice as an .xlsx (ArrayBuffer), or null when
  // the invoice carries no transaction-based lines (møtemat, rent/coffee only).
  // Sheet 1 repeats the invoice; sheet 2 lists every purchase behind its
  // subsidy, credit and pant lines. Refuses to build when the rows do not add
  // up to those lines — a specification that disagrees with its invoice must
  // never go to a customer.
  private invoiceSpecWorkbook(XLSX: any, inv: any, ctx: any): ArrayBuffer | null {
    if (inv.specSystemIds && !inv.specSystemIds.length) { return null; }
    const { I, byCustomer, systemName, groupName } = ctx;
    const r2 = (n: number) => +(+n || 0).toFixed(2);
    const rows = (byCustomer[inv.customerId] || [])
      .filter((r: any[]) => !inv.specSystemIds || inv.specSystemIds.includes(r[I.systemId] || null))
      .filter((r: any[]) => !inv.specGroupIds || inv.specGroupIds.includes(r[I.groupId]))
      .filter((r: any[]) => !inv.specExcludeGroupIds || !inv.specExcludeGroupIds.includes(r[I.groupId]))
      .sort((a: any[], b: any[]) => a[I.unix] - b[I.unix]);
    if (!rows.length) { return null; }

    const rate = inv.vatRate;
    const creditExVat = (cr: number) => rate > 0 ? cr / (1 + rate) : cr;
    const PAY_LABEL: { [k: string]: string } = { credit: 'Kreditt', gavekort: 'Gavekort', vipps: 'Vipps', stripe: 'Kort' };
    const tot = { sub: 0, cr: 0, crEx: 0, pant: 0 };
    const txRows: any[][] = [['Dato', 'Tid', 'Ordrenr', 'Navn', 'Ansattnr', 'System', 'Gruppe', 'Produkt', 'Betaling',
      'Subsidie eks. mva', 'Kreditt inkl. mva', 'Kreditt eks. mva', 'Pant (mva-fritt)', 'Fakturert eks. mva']];
    rows.forEach((r: any[]) => {
      const t = moment.unix(r[I.unix]).tz('Europe/Oslo');
      const sub = +r[I.sub] || 0, cr = +r[I.cr] || 0, pant = +r[I.pant] || 0;
      const crEx = creditExVat(cr);
      tot.sub += sub; tot.cr += cr; tot.crEx += crEx; tot.pant += pant;
      txRows.push([t.format('DD.MM.YYYY'), t.format('HH:mm'), r[I.orderId], r[I.buyer] || 'Gjest', r[I.employeeId],
        systemName[r[I.systemId]] || r[I.systemId] || '', groupName[r[I.groupId]] || r[I.groupId] || '', r[I.product],
        PAY_LABEL[r[I.payType]] || r[I.payType], r2(sub), r2(cr), r2(crEx), r2(pant), r2(sub + crEx + pant)]);
    });
    txRows.push([]);
    txRows.push([`Sum (${rows.length} transaksjoner)`, '', '', '', '', '', '', '', '',
      r2(tot.sub), r2(tot.cr), r2(tot.crEx), r2(tot.pant), r2(tot.sub + tot.crEx + tot.pant)]);

    const lineSum = (kind: string) => inv.lines.filter((l: any) => l.kind === kind).reduce((n: number, l: any) => n + (+l.amountExVat || 0), 0);
    [['subsidy', 'subsidie', tot.sub], ['credit', 'kreditt', tot.crEx], ['pant', 'pant', tot.pant]].forEach(([kind, label, sum]: any[]) => {
      if (Math.abs(lineSum(kind) - sum) > 1) {
        throw new Error(`Specification for ${inv.ref} (${inv.recipient.name}) does not match the invoice: ${label} ${r2(sum)} in the transactions vs ${r2(lineSum(kind))} on the invoice. Re-run the reconciliation for ${this.invoiceMonth}.`);
      }
    });

    const head: any[][] = [
      ['Spesifikasjon til faktura'],
      [],
      ['Kunde', inv.recipient.name],
      ['Org.nr', inv.recipient.orgId || ''],
      ['Fakturareferanse', inv.ref],
      ['Deres referanse', inv.customerReference || ''],
      ['Periode', `${moment(inv.periodFrom).format('DD.MM.YYYY')} – ${moment(inv.periodTo).format('DD.MM.YYYY')}`],
      [],
      ['Beskrivelse', 'Antall', 'Beløp eks. mva']
    ];
    inv.lines.forEach((l: any) => head.push([l.description, l.qty === '' ? '' : l.qty, r2(l.amountExVat)]));
    head.push([]);
    head.push(['Sum eks. mva', '', r2(inv.subtotalExVat)]);
    head.push(['Mva', '', r2(inv.vatAmount)]);
    if (inv.vatExemptTotal) { head.push(['Pant (mva-fritt)', '', r2(inv.vatExemptTotal)]); }
    head.push(['Totalt inkl. mva', '', r2(inv.totalIncVat)]);
    head.push([]);
    head.push(['Transaksjonene bak linjene for subsidie, kreditt og pant står på arket «Transaksjoner».']);

    const wb = XLSX.utils.book_new();
    const add = (aoa: any[][], name: string, widths: number[]) => {
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = widths.map(w => ({ wch: w }));
      XLSX.utils.book_append_sheet(wb, ws, name);
    };
    add(head, 'Faktura', [70, 10, 16]);
    add(txRows, 'Transaksjoner', [11, 6, 15, 26, 9, 26, 18, 32, 9, 15, 15, 15, 14, 16]);
    return XLSX.write(wb, { type: 'array', bookType: 'xlsx', compression: true });
  }

  private specFileName(inv: any) {
    const name = String(inv.recipient.name || '').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60);
    return `Spesifikasjon ${inv.ref} ${name}.xlsx`;
  }

  // Every invoice's specification in one zip, to check before anything is sent.
  async downloadInvoiceSpecs() {
    if (!this.invoiceMonth) { return; }
    this.exportingSpecs = true; this.reconError = '';
    try {
      const invoices = await this.buildInvoices();
      if (!invoices.length) { this.reconError = 'No billable customers to invoice for this month.'; return; }
      const [XLSX, JSZip] = await Promise.all([import('xlsx'), import('jszip').then(m => (m as any).default || m)]);
      const ctx = await this.specContext();
      const zip = new JSZip();
      let count = 0;
      invoices.forEach(inv => {
        const book = this.invoiceSpecWorkbook(XLSX, inv, ctx);
        if (book) { zip.file(this.specFileName(inv), book); count++; }
      });
      if (!count) { this.reconError = 'None of this month\'s invoices have transactions to specify.'; return; }
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `spesifikasjoner-${this.invoiceMonth}.zip`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      this.savedNote = `${count} of ${invoices.length} invoices have a transaction specification (the rest are møtemat, rent or coffee only).`;
    } catch (err) {
      this.reconError = (err && (err as any).message) || 'Could not build the specifications.';
    } finally {
      this.exportingSpecs = false;
    }
  }

  // Step 1 of sending: build the month's invoices and ask the server what each
  // would become in PowerOffice — read-only, nothing is created. The operator
  // then sees the environment (demo/production), every blocking problem and every
  // warning, and ticks which invoices to send. See powerOfficePreflight.js.
  async checkPowerOffice() {
    if (!this.invoiceMonth) { return; }
    this.checkingPowerOffice = true; this.powerOfficeError = ''; this.powerOfficeResults = null; this.poCheck = null;
    try {
      const built = await this.buildInvoices();
      if (!built.length) { this.powerOfficeError = 'No billable customers to invoice for this month.'; return; }
      const invoices = built.map(inv => ({ ...inv, lines: inv.lines.map((l: any) => ({ ...l, vatRate: lineVatRate(inv, l.kind) })) }));
      const res: any = await this.svc.preflightPowerOffice(invoices);
      if (!res || res.success === false) { this.powerOfficeError = (res && res.message) || 'The PowerOffice check failed.'; return; }
      const byRef: { [ref: string]: any } = {};
      invoices.forEach(inv => { byRef[inv.ref] = inv; });
      this.poCheck = {
        environment: res.environment,
        client: res.client,
        month: this.invoiceMonth,
        invoices: byRef,
        rows: res.invoices.map((r: any) => ({ ...r, customerName: r.customerName || this.customerName(r.customerId), selected: r.ok }))
      };
    } catch (err: any) {
      this.powerOfficeError = (err && err.error && err.error.message) || (err && err.message) || 'Could not check the invoices against PowerOffice.';
    } finally {
      this.checkingPowerOffice = false;
    }
  }

  get poSelected(): any[] { return this.poCheck ? this.poCheck.rows.filter(r => r.selected && r.ok && r.sent !== 'ok') : []; }
  get poSelectedTotal(): number { return this.poSelected.reduce((n, r) => n + (Number(r.ourTotal) || 0), 0); }
  get poBlockedCount(): number { return this.poCheck ? this.poCheck.rows.filter(r => !r.ok).length : 0; }
  get poReady(): any[] { return this.poCheck ? this.poCheck.rows.filter(r => r.ok && r.sent !== 'ok') : []; }
  get poReadyTotal(): number { return this.poReady.reduce((n, r) => n + (Number(r.ourTotal) || 0), 0); }

  sendCheckedToPowerOffice() { return this.sendRowsToPowerOffice(this.poSelected); }

  // Every error-free invoice not yet sent, after one confirmation. Production
  // still goes one customer per call (the server refuses more), in order.
  sendAllToPowerOffice() { return this.sendRowsToPowerOffice(this.poReady); }

  // One customer at a time (every invoice of that kundekort, e.g. split by
  // reference) — the only way to send in production, so each draft can be
  // checked in Go before the next. The server refuses more than one customer
  // per production call as well.
  sendCustomerToPowerOffice(row: any) {
    if (!this.poCheck) { return Promise.resolve(); }
    return this.sendRowsToPowerOffice(this.poCheck.rows.filter(r => r.customerId === row.customerId && r.ok && r.sent !== 'ok'));
  }

  // Step 2: create drafts for the given error-free invoices only, after an
  // explicit confirmation that names the environment and the PowerOffice client.
  // Production needs the word PRODUCTION typed. Nothing is emailed to customers;
  // a human reviews and sends each draft inside PowerOffice Go. The check panel
  // stays open, with sent rows marked, so the next customer can follow.
  private async sendRowsToPowerOffice(rows: any[]) {
    const check = this.poCheck;
    if (!check) { return; }
    if (check.month !== this.invoiceMonth) { this.powerOfficeError = 'The month changed after the check. Run the check again.'; return; }
    if (!rows.length) { return; }
    const total = rows.reduce((n, r) => n + (Number(r.ourTotal) || 0), 0);
    const customerCount = new Set(rows.map(r => r.customerId)).size;
    const who = customerCount === 1 ? ` for ${rows[0].customerName}` : ` for ${customerCount} customers`;
    const summary = `${rows.length} draft invoice(s)${who}, ${total.toFixed(2)} kr incl. VAT, ` +
      `in PowerOffice ${check.environment.toUpperCase()} — client "${check.client && check.client.name}".`;
    if (check.environment === 'production') {
      const typed = prompt(`${summary}\n\nThis writes to the real accounting system. Type PRODUCTION to continue.`);
      if ((typed || '').trim() !== 'PRODUCTION') { return; }
    } else if (!confirm(`Create ${summary}`)) {
      return;
    }

    this.sendingToPowerOffice = true; this.powerOfficeError = '';
    // Production: one call per customer, so the server's one-customer guard
    // holds; a failed customer is reported and the rest carry on.
    const batches: any[][] = [];
    if (check.environment === 'production') {
      const byCustomer = new Map<string, any[]>();
      rows.forEach(r => { byCustomer.set(r.customerId, [...(byCustomer.get(r.customerId) || []), r]); });
      byCustomer.forEach(b => batches.push(b));
    } else {
      batches.push(rows);
    }
    const errors: string[] = [];
    try {
      for (let i = 0; i < batches.length; i++) {
        this.poSendProgress = batches.length > 1 ? `${i + 1} / ${batches.length}` : '';
        const err = await this.createPowerOfficeDrafts(check, batches[i]);
        if (err) { errors.push(batches.length > 1 ? `${batches[i][0].customerName}: ${err}` : err); }
      }
    } finally {
      this.powerOfficeError = errors.join(' · ');
      this.poSendProgress = '';
      this.sendingToPowerOffice = false;
    }
  }

  // Creates the drafts for one batch and attaches each specification. Returns
  // an error message instead of throwing, so a batch loop can go on.
  private async createPowerOfficeDrafts(check: any, rows: any[]): Promise<string> {
    try {
      const invoices = rows.map(r => check.invoices[r.ref]);
      // Every specification is built BEFORE any draft exists: a month without
      // transaction rows, or a spec that disagrees with its invoice, stops here
      // instead of leaving drafts in PowerOffice that are missing their attachment.
      const XLSX = await import('xlsx');
      const ctx = await this.specContext();
      const specs: { [ref: string]: { fileName: string, book: ArrayBuffer } } = {};
      invoices.forEach(inv => {
        const book = this.invoiceSpecWorkbook(XLSX, inv, ctx);
        if (book) { specs[inv.ref] = { fileName: this.specFileName(inv), book }; }
      });

      const res: any = await this.svc.sendInvoicesToPowerOffice(invoices, check.environment);
      if (!res || res.success === false) {
        rows.forEach(r => { r.sent = 'failed'; });
        return (res && res.message) || 'Sending to PowerOffice failed.';
      }
      const results = res.results.map((r: any) => ({
        ...r,
        customerName: r.customerName || this.customerName(r.customerId),
        spec: r.ok && specs[r.ref] ? 'pending' : 'none'
      }));
      results.forEach((r: any) => {
        const row = check.rows.find(x => x.ref === r.ref);
        if (row) { row.sent = r.ok ? 'ok' : 'failed'; row.selected = false; }
      });
      this.powerOfficeResults = [...results, ...(this.powerOfficeResults || [])];
      // One upload per draft, one at a time — a failure is shown on its row and
      // never undoes the draft or stops the others.
      for (const r of results) {
        if (r.spec !== 'pending') { continue; }
        const spec = specs[r.ref];
        try {
          const up: any = await this.svc.attachSpecToPowerOffice(r.powerOfficeSalesOrderId, spec.fileName, this.toBase64(spec.book));
          if (!up || up.success === false) { throw new Error((up && up.message) || 'upload failed'); }
          r.spec = 'attached';
        } catch (e: any) {
          r.spec = 'failed';
          r.specError = (e && e.error && e.error.message) || (e && e.message) || 'upload failed';
        }
      }
      return results.some((r: any) => !r.ok) ? 'some invoices failed, see the results below' : '';
    } catch (err: any) {
      rows.forEach(r => { if (r.sent !== 'ok') { r.sent = 'failed'; } });
      return (err && err.error && err.error.message) || (err && err.message) || 'Could not send the invoices.';
    }
  }

  // The same buildInvoices() output as the API send, as PowerOffice's own
  // [Orders] import file — see PO_ORDER_COLUMNS above for what it is and is not.
  // One order per invoice: a header row carrying the customer, then one row per
  // invoice line, grouped by OrderNo. Numbers and the layout mirror
  // createDraftInvoice() in poweroffice.service.js (Quantity 1, the line amount
  // ex VAT as unit price, the order dated on the period's last day).
  async exportPowerOfficeImportFile() {
    if (!this.invoiceMonth) { return; }
    this.exportingPowerOfficeFile = true; this.powerOfficeError = ''; this.powerOfficeFileNote = '';
    try {
      const invoices = await this.buildInvoices();
      if (!invoices.length) { this.powerOfficeError = 'No billable customers to invoice for this month.'; return; }

      // Kundenr is what the import posts the order to. Without it Go would either
      // reject the file or create a new customer, so an invoice whose kundekort
      // has none (or has no kundekort at all) is LEFT OUT of the file and named
      // in a warning — the rest still export, so one unfinished kundekort never
      // holds up the month. Those invoices are the operator's to send another way.
      const kundenrOf = (inv: any) => String((this.customers.find(x => x.kundekortId === inv.customerId) || {}).powerOfficeCustomerNo || '').trim();
      const skipped = new Set<string>();
      const missingProduct = new Set<string>();
      const exportable = invoices.filter(inv => {
        if (!kundenrOf(inv)) { skipped.add(inv.recipient.name); return false; }
        inv.lines.forEach((l: any) => { if (!PO_PRODUCT_NO_BY_KIND[l.kind]) { missingProduct.add(l.kind); } });
        return true;
      });
      const skippedNote = skipped.size
        ? ` Left out, no PowerOffice customer number on the kundekort: ${Array.from(skipped).join(', ')} — invoice these separately or fill in Kundenr and export again.`
        : '';
      if (!exportable.length) {
        this.powerOfficeError = `Nothing to export.${skippedNote}`;
        return;
      }
      if (missingProduct.size) {
        this.powerOfficeError = `No PowerOffice product for line type "${Array.from(missingProduct).join('", "')}". Create the product in PowerOffice Go and map it in PO_PRODUCT_NO_BY_KIND.`;
        return;
      }

      const XLSX = await import('xlsx');
      const col: { [name: string]: number } = {};
      PO_ORDER_COLUMNS.forEach((c, i) => { col[c] = i; });
      const row = (cells: { [name: string]: any }) => {
        const r: any[] = new Array(PO_ORDER_COLUMNS.length).fill(null);
        Object.keys(cells).forEach(k => { r[col[k]] = cells[k]; });
        return r;
      };
      // The first cell names the import type; the second row is the header.
      const rows: any[][] = [['[Orders]'], PO_ORDER_COLUMNS.slice()];
      let lineCount = 0; let totalExVat = 0;
      exportable.forEach((inv, i) => {
        const orderNo = i + 1;
        // DDMMYYYY as a number, as the example file has it (leading zero dropped).
        const orderDate = Number(moment(inv.periodTo, 'YYYY-MM-DD').format('DDMMYYYY'));
        const kundenr = kundenrOf(inv);
        rows.push(row({
          OrderNo: orderNo,
          OrderDate: orderDate,
          SalesPersonEmployeeNo: PO_SALESPERSON_EMPLOYEE_NO,
          CustomerNo: /^\d+$/.test(kundenr) ? Number(kundenr) : kundenr,
          // Drives which sales account each product posts to, like the API path.
          IsVatFree: inv.vatRate > 0 ? 0 : 1,
          // "Deres referanse" — PowerOffice's Reference is the customer's order
          // number, which is what customerReference holds.
          Reference: (inv.customerReference || '').trim() || null,
          // Never merge: split billing and møtemat deliberately give one customer
          // several orders in the same file.
          MergeWithPreviousOrder: 0
        }));
        inv.lines.forEach((l: any, j: number) => {
          const amount = +(Number(l.amountExVat) || 0).toFixed(2);
          rows.push(row({
            OrderNo: orderNo,
            OrderDate: orderDate,
            ProductCode: PO_PRODUCT_NO_BY_KIND[l.kind],
            Quantity: 1,
            Description: l.description,
            OrderLineUnitPrice: amount,
            SortOrder: j + 1
          }));
          lineCount++; totalExVat += amount;
        });
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = PO_ORDER_COLUMNS.map(c => ({ wch: c === 'Description' ? 70 : 14 }));
      XLSX.utils.book_append_sheet(wb, ws, 'Orders');
      XLSX.writeFile(wb, `poweroffice-ordreimport-${this.invoiceMonth}.xlsx`);
      this.powerOfficeFileNote = `Import file: ${exportable.length} of ${invoices.length} invoices, ${lineCount} lines, ${totalExVat.toFixed(2)} ex VAT. `
        + 'Upload in PowerOffice Go under Regnskap → Import → Standardimport. Importing the same file twice creates every draft twice.';
      // Shown as a warning in its own right, so it is not lost in the note.
      if (skipped.size) { this.powerOfficeError = skippedNote.trim(); }
    } catch (err) {
      this.powerOfficeError = (err && (err as any).message) || 'Could not build the PowerOffice import file.';
    } finally {
      this.exportingPowerOfficeFile = false;
    }
  }

  private toBase64(buf: ArrayBuffer): string {
    const bytes = new Uint8Array(buf);
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
      bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)));
    }
    return btoa(bin);
  }

  private esc(v: any): string {
    return String(v == null ? '' : v).replace(/[&<>"]/g, (ch) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as any)[ch]);
  }
  private money(n: number): string {
    return (Number(n) || 0).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  private invoicesHtml(invoices: any[]): string {
    const pages = invoices.map(inv => {
      const vatPct = Math.round((inv.vatRate || 0) * 100);
      const rows = inv.lines.map((l: any) => `
        <tr>
          <td>${this.esc(l.description)}</td>
          <td class="num">${l.qty}</td>
          <td class="num">${this.money(l.amountExVat)}</td>
        </tr>`).join('');
      const r = inv.recipient;
      return `
      <section class="invoice">
        <header class="inv-head">
          <div class="seller">
            <h1>${this.esc(SELLER.name)}</h1>
            <div>${this.esc(SELLER.orgId)}</div>
            <div>${this.esc(SELLER.address)}</div>
            <div>${this.esc(SELLER.zipCity)}</div>
            <div>Tlf: ${this.esc(SELLER.phone)}</div>
            <div>${this.esc(SELLER.email)}</div>
          </div>
          <div class="meta">
            <h2>Faktura</h2>
            <div><span>Ref</span> ${this.esc(inv.ref)}</div>
            <div><span>Periode</span> ${this.esc(inv.periodFrom)} – ${this.esc(inv.periodTo)}</div>
            ${inv.ourReference ? `<div><span>Vår ref.</span> ${this.esc(inv.ourReference)}</div>` : ''}
            ${inv.customerReference ? `<div><span>Deres ref.</span> ${this.esc(inv.customerReference)}</div>` : ''}
          </div>
        </header>
        <div class="bill-to">
          <div class="lbl">Faktureres til</div>
          <strong>${this.esc(r.name)}</strong>
          ${r.orgId ? `<div>Org: ${this.esc(r.orgId)}</div>` : ''}
          ${r.address ? `<div>${this.esc(r.address)}</div>` : ''}
          ${r.address2 ? `<div>${this.esc(r.address2)}</div>` : ''}
          ${r.zipCity ? `<div>${this.esc(r.zipCity)}</div>` : ''}
          ${r.country ? `<div>${this.esc(r.country)}</div>` : ''}
          ${r.email ? `<div>${this.esc(r.email)}</div>` : ''}
        </div>
        <table class="lines">
          <thead><tr><th>Beskrivelse</th><th class="num">Antall</th><th class="num">Beløp eks. mva</th></tr></thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr><td colspan="2">Sum eks. mva</td><td class="num">${this.money(inv.subtotalExVat)}</td></tr>
            ${(inv.vatBreakdown && inv.vatBreakdown.length
              ? inv.vatBreakdown.map((b: any) =>
                  `<tr><td colspan="2">Mva ${Math.round(b.rate * 100)}% av ${this.money(b.base)}</td><td class="num">${this.money(b.vat)}</td></tr>`).join('')
              : `<tr><td colspan="2">Mva ${vatPct}%</td><td class="num">${this.money(inv.vatAmount)}</td></tr>`)}
            ${inv.vatExemptTotal ? `<tr><td colspan="2">Pant (mva-fritt)</td><td class="num">${this.money(inv.vatExemptTotal)}</td></tr>` : ''}
            <tr class="grand"><td colspan="2">Å betale (inkl. mva)</td><td class="num">${this.money(inv.totalIncVat)} kr</td></tr>
          </tfoot>
        </table>
        <footer class="pay">Betales til konto ${this.esc(SELLER.bankAccount)}. Merk betalingen med ref ${this.esc(inv.ref)}.</footer>
      </section>`;
    }).join('');
    return `<!doctype html><html lang="nb"><head><meta charset="utf-8"><title>Faktura ${this.esc(this.invoiceMonth)}</title>
      <style>
        * { box-sizing: border-box; }
        body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; margin: 0; font-size: 13px; }
        .invoice { padding: 24mm 18mm; page-break-after: always; min-height: 100vh; }
        .invoice:last-child { page-break-after: auto; }
        .inv-head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #222; padding-bottom: 12px; }
        .seller h1 { font-size: 18px; margin: 0 0 4px; }
        .meta { text-align: right; }
        .meta h2 { margin: 0 0 6px; font-size: 22px; letter-spacing: 1px; text-transform: uppercase; color: #555; }
        .meta span { color: #888; display: inline-block; min-width: 56px; }
        .bill-to { margin: 20px 0; }
        .bill-to .lbl { color: #888; text-transform: uppercase; font-size: 11px; letter-spacing: 1px; margin-bottom: 4px; }
        table.lines { width: 100%; border-collapse: collapse; margin-top: 8px; }
        table.lines th, table.lines td { padding: 8px 6px; text-align: left; }
        table.lines thead th { border-bottom: 1px solid #222; font-size: 11px; text-transform: uppercase; color: #555; }
        table.lines tbody td { border-bottom: 1px solid #eee; }
        .num { text-align: right; }
        table.lines tfoot td { padding-top: 8px; }
        table.lines tfoot tr.grand td { border-top: 2px solid #222; font-weight: bold; font-size: 15px; }
        .pay { margin-top: 24px; color: #555; font-size: 12px; }
        @media print { .invoice { min-height: auto; } }
      </style></head><body>${pages}</body></html>`;
  }

  // --- file parsing (SheetJS handles both .xlsx and .csv) ------------------
  private async readSheetRows(file: File): Promise<any[][]> {
    const XLSX = await import('xlsx');
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    return XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, raw: true }) as any[][];
  }

  // Amount cells may be numbers (xlsx) or comma-decimal strings (Stripe csv).
  private reconNum(v: any): number {
    if (v == null) { return 0; }
    if (typeof v === 'number') { return v; }
    const n = parseFloat(String(v).replace(/\s/g, '').replace(',', '.'));
    return isFinite(n) ? n : 0;
  }

  private async parseStripe(file: File): Promise<any[]> {
    const rows = await this.readSheetRows(file);
    if (!rows.length) { return []; }
    const header = (rows[0] || []).map((h: any) => String(h).trim());
    const col = (name: string) => header.indexOf(name);
    const iOrder = col('order_id (metadata)');
    const iDesc = col('Statement Descriptor');
    const iAmount = col('Amount');
    const iRef = col('Amount Refunded');
    const iCap = col('Captured');
    const iStatus = col('Status');
    const out: any[] = [];
    for (let r = 1; r < rows.length; r++) {
      const row = rows[r]; if (!row || !row.length) { continue; }
      let orderId: any = iOrder >= 0 ? row[iOrder] : '';
      if ((orderId == null || orderId === '') && iDesc >= 0) {
        const m = String(row[iDesc] || '').match(/frigg_(\d+)/);
        if (m) { orderId = m[1]; }
      }
      if (orderId == null || orderId === '') { continue; }
      out.push({
        provider: 'stripe',
        orderId: String(orderId).trim(),
        captured: String(row[iCap]).toLowerCase() === 'true',
        amount: this.reconNum(row[iAmount]),
        refunded: this.reconNum(row[iRef]),
        status: String(row[iStatus] || '')
      });
    }
    return out;
  }

  private async parseVipps(file: File): Promise<any[]> {
    const rows = await this.readSheetRows(file);
    // Vipps now exports the settlement report as a CSV with English headers and
    // no metadata preamble (header on the first row). Locate it by the
    // 'Order ID/Reference' column so we stay immune to any leading rows.
    let h = -1;
    for (let i = 0; i < rows.length; i++) {
      if ((rows[i] || []).some((c: any) => String(c).trim() === 'Order ID/Reference')) { h = i; break; }
    }
    if (h < 0) { throw new Error('Order ID/Reference column not found'); }
    const header = rows[h].map((c: any) => String(c).trim());
    const iOrder = header.indexOf('Order ID/Reference');
    const iType = header.indexOf('Type');
    const iGross = header.indexOf('Amount');
    const iNet = header.indexOf('Net amount');
    // The report mixes per-transaction rows (Capture/Refund, keyed by Frigg's
    // orderID) with settlement-only rows (Payout scheduled / Fees retained) that
    // carry no real orderID. Keep only the two we can reconcile, and normalize
    // the English type to the enum the cloud reconciler expects (Salg/Refusjon).
    const out: any[] = [];
    for (let r = h + 1; r < rows.length; r++) {
      const row = rows[r]; if (!row || !row.length) { continue; }
      const rawType = String(row[iType] || '').trim().toLowerCase();
      let type = '';
      if (rawType === 'capture') { type = 'Salg'; }
      else if (rawType === 'refund') { type = 'Refusjon'; }
      else { continue; }
      const orderId = row[iOrder];
      if (orderId == null || orderId === '') { continue; }
      out.push({
        provider: 'vipps',
        orderId: String(orderId).trim(),
        type,
        gross: this.reconNum(row[iGross]),
        net: this.reconNum(iNet >= 0 ? row[iNet] : 0)
      });
    }
    return out;
  }
}
